import {
  AttachmentMeta,
  FileAttachmentEntity,
  LinkEntity,
  MediaAttachmentEntity,
  MessageContentEntity,
  MessageEntity,
  MessageSendingStatus,
  ReactionSendingStatus,
  UploadState,
  VoiceAttachmentEntity,
  VoiceAttachmentMeta,
} from "@/indexedDB/types";
import {
  decryptMessageAttachment,
  decryptMessageContent,
  decryptServiceMessageContent,
  encryptFileChunk,
  encryptMessageAttachment,
  encryptMessageAttachmentMeta,
  encryptMessageContent,
} from "./crypto";
import { findMoovRange, computeChunkUploadOrder } from "@/lib/video/mp4BoxScan";
import { isDecryptionError, tryDecrypt } from "./decryptionError";
import {
  // addMessagesToLocalDB,
  addMessageToLocalDB,
  getMessageByClientIdFromLocalDB,
  getMessageByServerIdFromLocalDB,
  // getMessageCountByChatIdFromLocalDB,
  removeMessageByServerIdFromLocalDB,
  removeMessageByClientIdFromLocalDB,
  updateMessageByClientIdInLocalDB,
  updateMessageByServerIdInLocalDB,
} from "@/indexedDB/services/messages";
import { isUserAuthenticated } from "./auth";
import { getOrDecapsulateCSK } from "./cryptoCache";
import {
  deleteMessage,
  getMessage,
  // getMessages,
  patchMessage,
  pinMessage,
  postMessage,
} from "@/server/services/messageService";
import {
  EncryptedMessage,
  MessageCategory,
  MessageType,
} from "@/server/types/message";
import {
  getChatFromLocalDB,
  updateChatInLocalDB,
} from "@/indexedDB/services/chats";
// import { MESSAGE_LIST_PAGE_SIZE } from "@/config/constants";
import { LARGE_ATTACHMENT_THRESHOLD } from "@/config/constants";
import { runBlobSend } from "@/middleware/hooks/attachments/blobSendQueue";
import { isArchitectChat } from "@/utils/architectChat";
import { attachmentSizeRejection } from "@/utils/attachmentSizeLimit";
import { useSubscriptionState } from "@/zustand/SubscriptionState";
import { isCiphertext } from "@/lib/dataConversions";
import { useNavigationState } from "@/zustand/NavigationState";
import { ChatType } from "@/server/types/chat";
// import { processMessageLink } from "./links";
import type { RtfLinkType } from "./links";
import {
  addFileAttachmentToLocalDB,
  addMediaAttachmentToLocalDB,
  addVoiceAttachmentToLocalDB,
  getFileAttachmentContentFromLocalDB,
  getMediaAttachmentContentFromLocalDB,
  getVoiceAttachmentFromLocalDB,
  updateMediaAttachmentContent,
  removeMediaAttachmentFromLocalDB,
} from "@/indexedDB/services/attachments";
import { postAttachment } from "@/server/services/attachmentService";
import {
  startUpload,
  uploadPart,
  completeUpload,
  abortUpload,
} from "@/server/services/chunkedUploadService";
import { requestBlob } from "@/server/apiService";
import { ApiError } from "@/server/apiError";
import { processAttachmentBucketUrl } from "@/lib/utils";
import { isBlobReadable } from "@/lib/storage/blobLiveness";
import { getMediaPreviewMeta } from "@/utils/mediaPreviewMeta";
import { getStagedVideoDims } from "@/zustand/MediaMessageSenderState";
import { useRtfInputState } from "@/zustand/RtfInputState";
import {
  // addLinksToLocalDB,
  addLinkToLocalDB,
  // deleteLinkByIdFromLocalDB,
} from "@/indexedDB/services/links";
import {
  addPinnedMessageToLocalDB,
  getPinnedMessageByServerIdFromLocalDB,
  removePinnedMessageByServerIdFromLocalDB,
} from "@/indexedDB/services/pinnedMessages";
import {
  extractTimestampFromUUID7,
  generateUuid7,
  isValidUUID7,
} from "@/utils/uuid7";
import { useUploadProgressState } from "@/zustand/UploadProgressState";
import { useUploadControlState } from "@/zustand/UploadControlState";
import { acquireUploadSlot } from "@/lib/uploadSlots";
import { useUserAvatarMapState } from "@/zustand/UserAvatarMapState";
import { featureFlags } from "@/config/featureFlags";

const getAvatarId = (username: string) =>
  useUserAvatarMapState.getState().avatarMap[username]?.attachmentId || "";

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB

const MAX_UPLOAD_RETRIES = 3;

/**
 * Runs a chunked-upload mutation (`uploadPart` / `completeUpload`) through
 * transient failures with bounded linear backoff (1 s, 2 s, 3 s).
 *
 * Terminal (fail fast, no retry):
 *  - a user-initiated abort, and
 *  - any 4xx — every one the server emits on these routes is a deterministic,
 *    non-retryable decision: 400 (invalid part list), 403 (missing/invalid/
 *    expired upload token, membership revoked, or feature turned off).
 *
 * Retried (transient): everything else — 5xx (e.g. a GCS compose/metadata blip
 * on `completeUpload`, or a 503 AuthzUnavailable) and network errors. Retrying
 * is safe because both calls are idempotent server-side (a part re-upload
 * overwrites; a re-`complete` re-composes over the destination).
 */
async function runUploadCallWithRetry<T>(
  fn: () => Promise<T>,
  signal?: AbortSignal,
): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    if (signal?.aborted) {
      throw new Error("Upload aborted");
    }
    try {
      return await fn();
    } catch (err) {
      const isClientError =
        err instanceof ApiError &&
        err.status !== undefined &&
        err.status >= 400 &&
        err.status < 500;
      if (signal?.aborted || isClientError || attempt >= MAX_UPLOAD_RETRIES) {
        throw err;
      }
      await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
    }
  }
}

// TO DO = WE should deprecate this

export const processParentMessage = async ({
  messageId,
  chatId,
  category,
  eCSK,
  signal,
}: {
  messageId: string;
  chatId: string;
  category: MessageCategory;
  eCSK: string;
  signal?: AbortSignal;
}): Promise<MessageEntity | undefined> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  try {
    // TO DO => Check the implementation of "ForcedReconciliation" using this func
    // => if we check for existing msg in indexed db => Reactions workflow is broken
    // => reactions remain in pending state forever

    // TO DO => TTemporary removed => but will cause redundant requests for messages with same reply parent

    // check if msg already in indexxed db
    // const meessageInIndexedDB = await getMessageByIdFromLocalDB(messageId);
    // if (meessageInIndexedDB) {
    //   return undefined;
    // }

    let msgContent: MessageContentEntity;
    const reactions: Record<string, number> = {};

    const eMessage = await getMessage(
      {
        message_id: messageId,
        chat_id: chatId,
        category,
      },
      signal,
    );

    if (!eMessage) {
      return undefined;
    }

    const isReply = !!eMessage.parent_id;
    let decryptionFailed = false;
    let attemptedDecrypt = false;

    // decrypt  message content
    // TO DO => THIS IS TEMPORARY (Extract to better msg processing)
    if (eMessage.category === MessageCategory.DELETED) {
      return undefined;
    }

    if (!eCSK) {
      // Transient missing-eCSK: persist a placeholder so the parent renders
      // "Decrypting…" rather than disappearing, but don't burn a retry slot.
      decryptionFailed = true;
      msgContent = { text: "" };
    } else {
      attemptedDecrypt = true;
      const decrypted = await tryDecrypt(() =>
        decryptMessageContent(
          chatId,
          eMessage.key_epoch ?? 0,
          eCSK,
          eMessage.content,
          eMessage.attachment_meta,
        ),
      );
      if (decrypted.ok) {
        msgContent = decrypted.data;
      } else {
        decryptionFailed = true;
        msgContent = { text: "" };
      }

      // process reactions — skip undecryptable ones rather than failing the
      // entire parent message just because one reaction can't be decrypted.
      for (const [content, count] of Object.entries(eMessage.reactions)) {
        const isEncrypted = isCiphertext(content);
        if (isEncrypted) {
          try {
            const decryptedReaction = await decryptMessageContent(
              chatId,
              eMessage.key_epoch ?? 0,
              eCSK,
              content,
            );
            reactions[decryptedReaction.text] = count;
          } catch (error) {
            if (!isDecryptionError(error)) {
              throw error;
            }
            // Skip undecryptable reaction; parent renders without it.
          }
        } else {
          reactions[content] = count;
        }
      }
    }

    const isPinnedMessage =
      await getPinnedMessageByServerIdFromLocalDB(messageId);

    if (eMessage.sender && eMessage.sender_avatar) {
      useUserAvatarMapState
        .getState()
        .setUserAvatar(
          eMessage.sender,
          eMessage.sender_avatar,
          extractTimestampFromUUID7(eMessage.id),
        );
    }

    const msg: MessageEntity = {
      _clientId: eMessage.client_id || eMessage.id, // Use server message ID as client ID if client_id is not present
      id: eMessage.id,
      chatId: eMessage.chat_id,
      sender: eMessage.sender,
      avatarId: eMessage.sender_avatar,
      timestamp: extractTimestampFromUUID7(eMessage.id),
      category: eMessage.category,
      content: msgContent,
      hasAttachment: !!eMessage.attachment_meta,
      isEdited: eMessage.is_edited,
      isOutgoing: userFingerPrint.username === eMessage.sender,
      // comment related
      commentCount: eMessage.comment_count,
      senderAvatar: eMessage.sender_avatar,
      parentCommentCount: eMessage.parent_comment_count,
      // comment thread
      threadId: eMessage.thread_id,
      commentSentAt: null, // No longer needed with UUID7
      isReply,
      isPinned: !!isPinnedMessage,
      parentMessage: isReply
        ? {
            id: eMessage.parent_id,
            category: eMessage.parent_category,
          }
        : undefined,
      sendingStatus: MessageSendingStatus.SENT,
      reactions,
      userReaction:
        eMessage.user_reactions.length > 0
          ? {
              content: eMessage.user_reactions[0].content,
              messageId: eMessage.user_reactions[0].message_id,
              sendingStatus: ReactionSendingStatus.SUCCESS,
            }
          : undefined,
      ...(decryptionFailed
        ? {
            decryptionStatus: "failed" as const,
            // Only burn an attempt when we actually invoked a decryptor.
            ...(attemptedDecrypt ? { decryptionAttempts: 1 } : {}),
          }
        : { decryptionStatus: "ok" as const }),
      keyEpoch: eMessage.key_epoch ?? null,
    };

    return msg;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    return undefined;
  }
};

export const processMessageContent = async (
  chatId: string,
  eCSK: string,
  eMessages: EncryptedMessage[],
): Promise<MessageEntity[]> => {
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  const output: MessageEntity[] = [];

  for (const msg of eMessages) {
    let msgContent: MessageContentEntity;
    const reactions: Record<string, number> = {};
    const isReply = !!msg.parent_id;
    let decryptionFailed = false;
    // True only when we actually invoked a decryptor. Missing eCSK is
    // transient and shouldn't burn a retry-budget attempt.
    let attemptedDecrypt = false;

    // decrypt  message content
    // TO DO => THIS IS TEMPORARY (Extract to better msg processing)
    try {
      if (msg.category === MessageCategory.SERVICE) {
        attemptedDecrypt = true;
        msgContent = await decryptServiceMessageContent(msg);
      } else if (!eCSK) {
        // Transient: chat row exists but secure_payload not synced yet.
        // Persist as failed, but don't count this as a retry attempt — the
        // orchestrator will start counting once keys arrive.
        decryptionFailed = true;
        msgContent = { text: "" };
      } else {
        attemptedDecrypt = true;
        msgContent = await decryptMessageContent(
          chatId,
          msg.key_epoch ?? 0,
          eCSK,
          msg.content,
          msg.attachment_meta,
        );

        // process reactions
        if (msg.reactions) {
          for (const [content, count] of Object.entries(msg.reactions)) {
            const isEncrypted = isCiphertext(content);
            if (isEncrypted) {
              try {
                const decryptedReaction = await decryptMessageContent(
                  chatId,
                  msg.key_epoch ?? 0,
                  eCSK,
                  content,
                );
                reactions[decryptedReaction.text] = count;
              } catch (error) {
                if (!isDecryptionError(error)) {
                  throw error;
                }
                // Skip undecryptable reactions; the message itself can still render.
              }
            } else {
              reactions[content] = count;
            }
          }
        }
      }
    } catch (error) {
      if (!isDecryptionError(error)) {
        throw error;
      }
      decryptionFailed = true;
      msgContent = { text: "" };
    }

    const isPinnedMessage = await getPinnedMessageByServerIdFromLocalDB(msg.id);

    const _msg: MessageEntity = {
      _clientId: msg.client_id || msg.id, // Use client_id if present, otherwise use server id
      id: msg.id,
      chatId: msg.chat_id,
      sender: msg.sender,
      avatarId: msg.sender_avatar,
      timestamp: extractTimestampFromUUID7(msg.id),
      category: msg.category,
      content: msgContent,
      hasAttachment: !!msg.attachment_meta,
      isEdited: msg.is_edited,
      isOutgoing: userFingerPrint.username === msg.sender,
      // comment related
      commentCount: msg.comment_count,
      senderAvatar: msg.sender_avatar,
      parentCommentCount: msg.parent_comment_count,
      // comment thread
      threadId: msg.thread_id,
      commentSentAt: null, // No longer needed with UUID7
      isReply,
      isPinned: !!isPinnedMessage,
      parentMessage: isReply
        ? {
            id: msg.parent_id,
            category: msg.parent_category,
          }
        : undefined,
      tags: msg.tags?.length ? msg.tags : undefined,
      sendingStatus: MessageSendingStatus.SENT,
      reactions,
      userReaction:
        msg.user_reactions?.length > 0
          ? {
              content: msg.user_reactions[0].content,
              messageId: msg.user_reactions[0].message_id,
              sendingStatus: ReactionSendingStatus.SUCCESS,
            }
          : undefined,
      ...(decryptionFailed
        ? {
            decryptionStatus: "failed" as const,
            // Only burn an attempt when we actually invoked a decryptor.
            ...(attemptedDecrypt ? { decryptionAttempts: 1 } : {}),
          }
        : { decryptionStatus: "ok" as const }),
      keyEpoch: msg.key_epoch ?? null,
    };

    output.push(_msg);
  }

  // Batch-update the user avatar map from processed messages
  const avatarEntries = eMessages
    .filter((m) => m.sender && m.sender_avatar)
    .map((m) => ({
      username: m.sender,
      attachmentId: m.sender_avatar!,
      timestamp: extractTimestampFromUUID7(m.id),
    }));
  if (avatarEntries.length > 0) {
    useUserAvatarMapState.getState().setUserAvatarBatch(avatarEntries);
  }

  // Validate that all message IDs are proper UUID7 format
  const validatedOutput = validateMessageIds(output);

  return validatedOutput;
};

// deprecated since new UMBRA version of BE
// export const processMessages = async ({
//   chatId,
//   eCSK,
//   take,
//   takeBefore,
//   signal,
// }: {
//   chatId: string;
//   eCSK: string;
//   take?: number;
//   takeBefore?: string;
//   signal?: AbortSignal;
// }): Promise<MessageEntity[]> => {
//   // extra check on abort controller
//   if (signal?.aborted) {
//     return [];
//   }
//   // extra check if auth is present
//   const userFingerPrint = isUserAuthenticated();
//   if (!userFingerPrint) {
//     throw new Error("Auth not present");
//   }

//   try {
//     let output: MessageEntity[] = [];

//     const eMessages = await getMessages(
//       {
//         chat_id: chatId,
//         take,
//         sent_before: takeBefore,
//         // Main chat history should include normal messages and service messages.
//         // Comments are loaded separately via the threads middleware.
//         category: MessageCategory.MESSAGE,
//         category1: MessageCategory.SERVICE,
//       },
//       signal,
//     );

//     if (!eMessages.length) {
//       return output;
//     }

//     output = await processMessageContent(chatId, eCSK, eMessages);

//     return output;
//     // eslint-disable-next-line @typescript-eslint/no-unused-vars
//   } catch (error) {
//     return [];
//   }
// };

// deprecated since new UMBRA version of BE
// export const progressivelyLoadMessages = async (
//   chatId: string,
//   max?: number,
//   signal?: AbortSignal,
// ) => {
//   // extra check on abort controller
//   if (signal?.aborted) {
//     return;
//   }
//   // Check for user authentication.
//   const userFingerPrint = isUserAuthenticated();
//   if (!userFingerPrint) {
//     throw new Error("Auth not present");
//   }

//   // Global state references
//   const setActiveChatInitialMessagesLoaded =
//     useNavigationState.getState().setActiveChatInitialMessagesLoaded;
//   const setActiveChatFullMessageListLoaded =
//     useNavigationState.getState().setActiveChatFullMessageListLoaded;
//   const setActiveChatBatchCount =
//     useNavigationState.getState().setActiveChatBatchCount;
//   const setActiveChatRenderedBatchCount =
//     useNavigationState.getState().setActiveChatRenderedBatchCount;

//   try {
//     const localDbChat = await getChatFromLocalDB(chatId);

//     if (!localDbChat) {
//       return;
//     }
//     // TO DO => implement caching with TTL on CSKs
//     const eCSK = localDbChat.eCSK;

//     // if all msg are loaded return // TO DO => move to local store for faster access
//     if (localDbChat.fullMsgListInit) {
//       // Ensure loading flag is set even on early return — covers cases where
//       // persisted state or setActiveChatId had a stale value
//       const activeChatId = useNavigationState.getState().activeChatId;
//       if (chatId === activeChatId) {
//         setActiveChatInitialMessagesLoaded(true);
//         setActiveChatFullMessageListLoaded(true);
//       }
//       return;
//     }

//     // if first phase not completed
//     if (!localDbChat.firstPhaseMsgListInit) {
//       // Fetch a batch starting at the current offset.
//       const firstBatch = await processMessages({
//         chatId,
//         eCSK,
//         take: MESSAGE_LIST_PAGE_SIZE,
//         signal,
//       });

//       if (signal?.aborted) {
//         return;
//       }

//       if (firstBatch.length > 0) {
//         // add messagews to local db
//         await addMessagesToLocalDB(firstBatch);
//         // Mark the first loading phase as complete.
//         await updateChatInLocalDB(chatId, {
//           firstMessageId: firstBatch[firstBatch.length - 1].id,
//           firstPhaseMsgListInit: true,
//           lastSyncedAt: new Date().toISOString(),
//           batchCount: 1, // first batch
//         });

//         // if chat id is active
//         const activeChatId = useNavigationState.getState().activeChatId;
//         if (chatId === activeChatId) {
//           await updateChatInLocalDB(chatId, {
//             renderedBatchCount: 1, // first rendered batch
//           });

//           setActiveChatInitialMessagesLoaded(true);
//           setActiveChatBatchCount(1); // first batch
//           setActiveChatRenderedBatchCount(1); // first rendered batch
//         }

//         // process parent msg if replies found
//         const parentMsgs: MessageEntity[] = [];
//         // process links with no metadata
//         const links: LinkEntity[] = [];

//         for (const msg of firstBatch) {
//           // TO DO => check if parent msg already processed - to avoid extra request
//           if (msg.isReply) {
//             // check if parent msg is already proceesed in this batch
//             const parentIndex = firstBatch.findIndex(
//               (fbMsg) =>
//                 fbMsg.id === msg.parentMessage?.id &&
//                 fbMsg.timestamp === msg.parentMessage?.timestamp,
//             );

//             // if parent msg not found in batch
//             if (parentIndex === -1) {
//               const parentMsg = await processParentMessage({
//                 messageId: msg.parentMessage?.id || "",
//                 category: msg.parentMessage?.category || 0,
//                 chatId,
//                 eCSK,
//               });
//               if (parentMsg) {
//                 parentMsgs.push(parentMsg);
//               }
//             }
//           }

//           if (msg.content.link) {
//             const linkData = await processMessageLink({
//               messageId: msg.id,
//               chatId,
//               timestamp: msg.timestamp,
//               link: msg.content.link,
//               withMetadata: true,
//               signal,
//             });

//             if (linkData) {
//               links.push(linkData);
//             }
//           }
//         }

//         if (parentMsgs.length > 0) {
//           addMessagesToLocalDB(parentMsgs);
//         }
//         if (links.length > 0) {
//           addLinksToLocalDB(links);
//         }

//         // if first batch is less than page size, mark the whole list as complete and exit
//         if (firstBatch.length < MESSAGE_LIST_PAGE_SIZE) {
//           await updateChatInLocalDB(chatId, {
//             fullMsgListInit: true,
//             lastSyncedAt: new Date().toISOString(),
//           });

//           // if chat id is active
//           if (chatId === activeChatId) {
//             setActiveChatFullMessageListLoaded(true);
//           }

//           return;
//         }
//       } else {
//         // if no msg in first batch mark everything as complete
//         await updateChatInLocalDB(chatId, {
//           firstPhaseMsgListInit: true,
//           fullMsgListInit: true,
//           lastSyncedAt: new Date().toISOString(),
//         });

//         // if chat id is active
//         const activeChatId = useNavigationState.getState().activeChatId;
//         if (chatId === activeChatId) {
//           setActiveChatFullMessageListLoaded(true);
//           setActiveChatInitialMessagesLoaded(true);
//         }

//         return;
//       }
//     }

//     let moreMessages: MessageEntity[] = [];
//     let currentMsgCount: number;

//     do {
//       // extra check on abort controller
//       if (signal?.aborted) {
//         return;
//       }

//       const _localDbChat = await getChatFromLocalDB(chatId);
//       if (!_localDbChat) {
//         return;
//       }

//       moreMessages = await processMessages({
//         chatId,
//         eCSK,
//         take: MESSAGE_LIST_PAGE_SIZE,
//         takeBefore: _localDbChat.firstMessageId,
//         signal,
//       });

//       // Exit loop if no messages are returned
//       if (!moreMessages.length) {
//         break;
//       }

//       await updateChatInLocalDB(chatId, {
//         firstMessageId: moreMessages[moreMessages.length - 1].id,
//         lastSyncedAt: new Date().toISOString(),
//         batchCount: (_localDbChat.batchCount || 0) + 1, // increment batch count
//       });

//       const activeChatId = useNavigationState.getState().activeChatId;
//       if (chatId === activeChatId) {
//         setActiveChatBatchCount((_localDbChat.batchCount || 0) + 1); // increment batch count
//       }

//       // add messagews to local db
//       await addMessagesToLocalDB(moreMessages);

//       // process links with no metadata
//       const links: LinkEntity[] = [];
//       // process parent msg if replies found
//       const parentMsgs: MessageEntity[] = [];

//       for (const msg of moreMessages) {
//         // TO DO => check if parent msg already processed - to avoid extra request
//         if (msg.isReply) {
//           // check if parent msg is already proceesed in this batch
//           const parentIndex = moreMessages.findIndex(
//             (fbMsg) =>
//               fbMsg.id === msg.parentMessage?.id &&
//               fbMsg.timestamp === msg.parentMessage?.timestamp,
//           );

//           // if parent msg not found in batch
//           if (parentIndex === -1) {
//             const parentMsg = await processParentMessage({
//               messageId: msg.parentMessage?.id || "",
//               category: msg.parentMessage?.category || 0,
//               chatId,
//               eCSK,
//             });
//             if (parentMsg) {
//               parentMsgs.push(parentMsg);
//             }
//           }
//         }

//         if (msg.content.link) {
//           const linkData = await processMessageLink({
//             messageId: msg.id,
//             chatId,
//             timestamp: msg.timestamp,
//             link: msg.content.link,
//             withMetadata: true,
//             signal,
//           });

//           if (linkData) {
//             links.push(linkData);
//           }
//         }
//       }

//       if (parentMsgs.length > 0) {
//         addMessagesToLocalDB(parentMsgs);
//       }
//       if (links.length > 0) {
//         addLinksToLocalDB(links);
//       }

//       // if result is less than MESSAGE_LIST_PAGE_SIZE during loop - exit
//       if (moreMessages.length < MESSAGE_LIST_PAGE_SIZE) {
//         //  Finalize the state by marking progressive init as finished.
//         await updateChatInLocalDB(chatId, {
//           fullMsgListInit: true,
//         });
//         // if chat id is active
//         const _activeChatId = useNavigationState.getState().activeChatId;
//         if (chatId === _activeChatId) {
//           setActiveChatFullMessageListLoaded(true);
//         }
//         break;
//       }

//       currentMsgCount = await getMessageCountByChatIdFromLocalDB(chatId);
//     } while (moreMessages.length > 0 && currentMsgCount <= (max || 5000));

//     // TODO: Discuss with Andrei:
//     //  - why updating lastSyncedAt here?

//     // Because  -> //  Finalize the loop by setting lastSyncedAt at the time of finishing the loop.
//     // Also  -> //  Finalize the loop by marking progressive init as finished in local state.

//     await updateChatInLocalDB(chatId, {
//       fullMsgListInit: true,
//       lastSyncedAt: new Date().toISOString(),
//     });
//     // if chat id is active
//     const activeChatId = useNavigationState.getState().activeChatId;
//     if (chatId === activeChatId) {
//       setActiveChatFullMessageListLoaded(true);
//     }
//   } catch (error) {
//     // On error, still mark initial messages as loaded so the UI doesn't stay
//     // permanently stuck on "Loading conversation history..."
//     const activeChatId = useNavigationState.getState().activeChatId;
//     if (chatId === activeChatId) {
//       setActiveChatInitialMessagesLoaded(true);
//     }

//     if (error instanceof Error && error.message) {
//       throw error;
//     }
//     throw new Error("Cannot load message list.");
//   }
// };

// export const appendMoreMessages = async (
//   chatId: string,
//   signal?: AbortSignal,
// ) => {
//   // extra check on abort controller
//   if (signal?.aborted) {
//     return;
//   }
//   // Check for user authentication.
//   const userFingerPrint = isUserAuthenticated();
//   if (!userFingerPrint) {
//     throw new Error("Auth not present");
//   }

//   if (!chatId) {
//     return;
//   }

//   // Global state references
//   const activeChatId = useNavigationState.getState().activeChatId;

//   const setActiveChatHasMoreMessages =
//     useNavigationState.getState().setActiveChatHasMoreMessages;

//   const setActiveChatIsLoadingMessages =
//     useNavigationState.getState().setActiveChatIsLoadingMessages;

//   const localDbChat = await getChatFromLocalDB(chatId);

//   if (!localDbChat) {
//     return;
//   }

//   try {
//     await updateChatInLocalDB(chatId, {
//       isLoadingMessages: true,
//     });

//     // if on active chat id
//     if (chatId === activeChatId) {
//       setActiveChatIsLoadingMessages(true);
//     }

//     // TO DO => implement caching with TTL on CSKs
//     const eCSK = localDbChat.eCSK;

//     const messages = await processMessages({
//       chatId,
//       eCSK,
//       takeBefore: localDbChat.firstMessageTimestamp,
//       take: BE_MAX_LIMIT_MESSAGES_PER_REQUEST,
//       signal,
//     });

//     // if we have messages
//     if (messages.length > 0) {
//       // add messagews to local db
//       await addMessagesToLocalDB(messages);
//       // update new timestamp for first message and 'hasmoremessages' key
//       await updateChatInLocalDB(chatId, {
//         firstMessageTimestamp: messages[messages.length - 1].timestamp,
//         hasMoreMessages:
//           messages.length < BE_MAX_LIMIT_MESSAGES_PER_REQUEST ? false : true,
//       });

//       // if on active chat id
//       if (chatId === activeChatId) {
//         setActiveChatHasMoreMessages(
//           messages.length < BE_MAX_LIMIT_MESSAGES_PER_REQUEST ? false : true,
//         );
//       }

//       // process parent msg if replies found
//       const parentMsgs: MessageEntity[] = [];
//       // process links with no metadata
//       const links: LinkEntity[] = [];

//       for (const msg of messages) {
//         // TO DO => check if parent msg already processed - to avoid extra request
//         if (msg.isReply) {
//           // check if parent msg is already proceesed in this batch
//           const parentIndex = messages.findIndex(
//             (fbMsg) =>
//               fbMsg.id === msg.parentMessage?.id &&
//               fbMsg.timestamp === msg.parentMessage?.timestamp,
//           );

//           // if parent msg not found in batch
//           if (parentIndex === -1) {
//             const parentMsg = await processParentMessage({
//               messageId: msg.parentMessage?.id || "",
//               timestamp: msg.parentMessage?.timestamp || "",
//               category: msg.parentMessage?.category || 0,
//               chatId,
//               eCSK,
//             });
//             if (parentMsg) {
//               parentMsgs.push(parentMsg);
//             }
//           }
//         }

//         if (msg.content.link) {
//           const linkData = await processMessageLink({
//             messageId: msg.id,
//             chatId,
//             timestamp: msg.timestamp,
//             link: msg.content.link,
//             signal,
//           });

//           if (linkData) {
//             links.push(linkData);
//           }
//         }

//         if (parentMsgs.length > 0) {
//           addMessagesToLocalDB(parentMsgs);
//         }
//         if (links.length > 0) {
//           addLinksToLocalDB(links);
//         }
//       }

//       // if no messages
//     } else {
//       // update in chat in local db
//       await updateChatInLocalDB(chatId, {
//         hasMoreMessages: false,
//       });

//       // if on active chat id
//       if (chatId === activeChatId) {
//         setActiveChatHasMoreMessages(false);
//       }
//     }

//     // update in chat in local db
//     await updateChatInLocalDB(chatId, {
//       isLoadingMessages: false,
//     });

//     // if on active chat id
//     if (chatId === activeChatId) {
//       setActiveChatIsLoadingMessages(false);
//     }
//   } catch (error) {
//     // update in chat in local db
//     await updateChatInLocalDB(chatId, {
//       isLoadingMessages: false,
//     });

//     // if on active chat id
//     if (chatId === activeChatId) {
//       setActiveChatIsLoadingMessages(false);
//     }
//     if (error instanceof Error && error.message) {
//       throw error;
//     }
//     throw new Error("Cannot load message list.");
//   }
// };

/**
 * Settle an in-session send failure. A POST that threw may still have COMMITTED
 * server-side (its response was lost) — the same ambiguity the boot reconciliation
 * resolves (#1177). For a non-chunked send whose server id was never stamped
 * (`id === _clientId`, no `uploadState`), flag the row `deliveryUnconfirmed` so it
 * is NOT offered a destructive local delete of a possibly-delivered message; the
 * boot reconciliation re-confirms it by `client_id` and resolves it to a real
 * DELIVERED/READ or a deletable FAILED. Chunked / already-published rows keep the
 * plain FAILED they had before (their completion is the attachment-status feature's).
 */
const markSendFailed = async (_clientId: string): Promise<void> => {
  let row: MessageEntity | undefined;
  try {
    row = await getMessageByClientIdFromLocalDB(_clientId);
  } catch {
    row = undefined;
  }
  const ambiguous =
    !!row &&
    row.id === row._clientId &&
    !row.content?.media?.uploadState &&
    !row.content?.file?.uploadState &&
    !row.content?.voice?.uploadState;
  // Always write the flag (never merge-and-leave): a non-ambiguous failure must
  // CLEAR any prior `deliveryUnconfirmed` so a chunked/published row can't stay
  // stuck undeletable. Matches the reconciliation's not_found branch.
  await updateMessageByClientIdInLocalDB(_clientId, {
    sendingStatus: MessageSendingStatus.FAILED,
    deliveryUnconfirmed: ambiguous,
  });
};

export const postNewMessageInChat = async ({
  chatId,
  content,
  signal,
  linkMetadata: linkMetadataOverride,
}: {
  chatId: string;
  content: string;
  signal?: AbortSignal;
  linkMetadata?: RtfLinkType;
}): Promise<void> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  // if no payload
  if (!chatId || !content) {
    return;
  }

  // check if chat exists in local db
  const chatDetails = await getChatFromLocalDB(chatId);
  if (chatId !== chatDetails?.id) {
    return;
  }

  // if chat id is active
  const activeChatId = useNavigationState.getState().activeChatId;
  if (chatId !== activeChatId) {
    return;
  }

  const activeThreadId = useNavigationState.getState().activeThreadId;

  // // generate client message id for optimistic update
  const _clientId = generateUuid7(); // Temporary placeholder
  const now = extractTimestampFromUUID7(_clientId);

  // TO DO => PREVENT DOUBLE updates in LOCAL DB (middleware/socket events) -
  try {
    // OPTIMISTIC STUFF

    // Prefer metadata passed by the caller (captured before UI state was
    // cleared for responsiveness).  Fall back to Zustand for callers that
    // don't pass it explicitly.
    const linkMessageMetadata =
      linkMetadataOverride ??
      useRtfInputState.getState().linkMessagesMetadata[chatId];
    const clearLinkMessageMetadata =
      useRtfInputState.getState().clearLinkMessageMetadata;

    // TO DO => we can have a better state management for link metadata -
    // process ansd save it on socket event
    // with the new content mesg implementation

    // link processing
    if (linkMessageMetadata) {
      const processedLink: LinkEntity = {
        id: _clientId,
        chatId,
        timestamp: now,
        url: linkMessageMetadata.url,
        domainUrl: linkMessageMetadata.domainUrl,
        faviconUrl: linkMessageMetadata.faviconUrl,
        metaTitle: linkMessageMetadata.title,
        metaDescription: linkMessageMetadata.description,
        ogImage: linkMessageMetadata.image,
        withMetadata: true,
      };

      await addLinkToLocalDB(processedLink);
      clearLinkMessageMetadata(chatId);
    }

    // create new msg entity for optimistic update in local db
    const messageEntity: MessageEntity = {
      _clientId, // Local-only ID for tracking the optimistic message
      id: _clientId, // Temporary ID for optimistic update
      chatId,
      sender: userFingerPrint.username,
      avatarId: getAvatarId(userFingerPrint.username),
      timestamp: now, // We can use the current timestamp for optimistic UI. The server timestamp will be updated once we get the response.
      category: activeThreadId
        ? MessageCategory.COMMENT // it is actually the comment replies
        : MessageCategory.MESSAGE,
      content: {
        text: content,
        link: linkMessageMetadata?.url,
      },
      hasAttachment: false,
      isEdited: false,
      isOutgoing: true,
      isReply: false,
      // comment related
      commentCount: null,
      senderAvatar: getAvatarId(userFingerPrint.username),
      parentCommentCount: null,
      // TO DO => Modify function to accept thread id if replying in thread
      threadId: activeThreadId,
      commentSentAt: null, // No longer needed with UUID7
      isPinned: false,
      sendingStatus: MessageSendingStatus.PENDING,
      keyEpoch: chatDetails.currentEpoch ?? null,
    };

    await addMessageToLocalDB(messageEntity);

    // SERVER STUFF

    // encrypt content
    const { encryptedContent, key_epoch } = await encryptMessageContent(
      chatDetails.eCSK,
      chatDetails.currentEpoch ?? 0,
      chatId,
      content,
    );

    // TO DO => create separate interface for encrypted message payload - to avoid confusion with EncryptedMessage from server which has more fields and some different field names
    // create new encrypted msg payload for posting to server
    const encryptedMessage: Partial<EncryptedMessage> = {
      // id: newMessageId,
      client_id: _clientId, // Pass the client-generated ID to the server for tracking
      chat_id: chatId,
      sender: userFingerPrint.username,
      content: encryptedContent,
      key_epoch,
      is_edited: false,
      category: activeThreadId
        ? MessageCategory.COMMENT
        : MessageCategory.MESSAGE,
      attachment_meta: "",
      thread_id: activeThreadId,
      parent_comment_count: null,
    };

    const messageType =
      chatDetails.type === ChatType.GROUP
        ? MessageType.GROUP
        : chatDetails.type === ChatType.P2P
          ? MessageType.P2P
          : MessageType.NOTE;

    const serverMessageIds = await postMessage(messageType, encryptedMessage);

    const serverMessageId = serverMessageIds[0];
    const serverMessageTimestamp = extractTimestampFromUUID7(serverMessageId);

    // check for race condition where message status is already "DELIVERED" or "READ" by socket event before we get response from server
    const localDbMessage = await getMessageByClientIdFromLocalDB(_clientId);
    if (
      localDbMessage &&
      (localDbMessage.sendingStatus === MessageSendingStatus.DELIVERED ||
        localDbMessage.sendingStatus === MessageSendingStatus.READ)
    ) {
      // if msg is already DELIVERED or READ, we  skip the update to SENT status
      return;
    }

    await updateMessageByServerIdInLocalDB(_clientId, {
      id: serverMessageId,
      timestamp: serverMessageTimestamp,
      sendingStatus: MessageSendingStatus.SENT,
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // TO DO => check if we have to remove any links from db
    // Update by _clientId (the immutable primary key) rather than the mutable
    // `id` index: once a message publishes, its `id` may be stamped to the
    // server id, so an index lookup keyed on _clientId would match nothing and
    // silently leave the row stuck PENDING instead of FAILED.
    await markSendFailed(_clientId);
    // Preserve the original failure as `cause` so telemetry (AutoErrorReport)
    // can see WHY the send failed (encryption, chunked upload, API, socket, …),
    // instead of collapsing every distinct failure into one opaque cluster.
    throw new Error("Error sending message", { cause: error });
  }
};

/**
 * Edits an existing message in a chat with support for attachment operations
 *
 * How it works:
 * 1. Validates input parameters and authentication
 * 2. Handles optimistic updates to the local database
 * 3. Encrypts the new content and prepares the message update
 * 4. Sends the update to the server
 * 5. Handles error cases with rollback to original state
 *
 * Key features:
 * - Supports text content changes
 * - Handles attachment removal
 * - Supports replacing existing attachments with new ones
 * - Handles adding attachments to text-only messages
 */
export const editMessageInChat = async ({
  chatId,
  message,
  newContent,
  removeAttachment,
  newMediaAttachment,
  newFileAttachment,
  keepExistingAttachment,
  signal,
}: {
  chatId: string;
  message: MessageEntity;
  newContent: string;
  removeAttachment?: boolean;
  newMediaAttachment?: {
    file: File;
    attachmentId: string;
    thumbnailId: string;
    thumbnailBlob: Blob;
  };
  newFileAttachment?: {
    file: File;
    attachmentId: string;
  };
  keepExistingAttachment?: boolean;
  signal?: AbortSignal;
}): Promise<void> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // if no payload
  if (!chatId || !message) {
    return;
  }

  // Allow empty content when there's an attachment
  if (
    !newContent &&
    !newMediaAttachment &&
    !newFileAttachment &&
    !removeAttachment &&
    !keepExistingAttachment
  ) {
    return;
  }

  // if chat id is active
  const activeChatId = useNavigationState.getState().activeChatId;
  if (chatId !== activeChatId || chatId !== message.chatId) {
    return;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  // TO DO => PREVENT DOUBLE updates in LOCAL DB (middleware/socket events) -
  try {
    // check if chat exists in local db
    const chatDetails = await getChatFromLocalDB(chatId);
    if (!chatDetails) {
      return;
    }

    // check if existing link + metadata saved in rtf state
    // const linkMessage = useRtfInputState.getState().linkMessages[chatId];
    // const linkMessageMetadata =
    //   useRtfInputState.getState().linkMessagesMetadata[chatId];

    // OPTIMISTIC STUFF

    // link processing
    // if (has_links) {
    //   const processedLink: LinkEntity = {
    //     id: message.id,
    //     chatId,
    //     timestamp: message.timestamp,
    //     url: linkMessageMetadata.url,
    //     domainUrl: linkMessageMetadata.domainUrl,
    //     faviconUrl: linkMessageMetadata.faviconUrl,
    //     metaTitle: linkMessageMetadata.title,
    //     metaDescription: linkMessageMetadata.description,
    //     ogImage: linkMessageMetadata.image,
    //     withMetadata: true,
    //   };

    //   await addLinkToLocalDB(processedLink);
    //   clearLinkMessageMetadata(chatId);
    // }

    // Process attachments if needed
    let attachmentMeta: AttachmentMeta | undefined;
    let encryptedAttachmentMeta: string | undefined;

    // Handle attachment operations
    if (removeAttachment) {
      // Case 1: Remove existing attachment
      attachmentMeta = undefined;
      encryptedAttachmentMeta = undefined;
    } else if (keepExistingAttachment) {
      // Case 2: Keep existing attachment (only editing text)
      // Extract the existing attachment metadata from the message
      if (message.content?.media) {
        // For media attachments
        attachmentMeta = message.content.media;
        // Re-encrypt the existing attachment metadata
        encryptedAttachmentMeta = await encryptMessageAttachmentMeta(
          chatDetails.eCSK,
          chatDetails.currentEpoch ?? 0,
          chatId,
          attachmentMeta,
        );
      } else if (message.content?.file) {
        // For file attachments
        attachmentMeta = message.content.file;
        // Re-encrypt the existing attachment metadata
        encryptedAttachmentMeta = await encryptMessageAttachmentMeta(
          chatDetails.eCSK,
          chatDetails.currentEpoch ?? 0,
          chatId,
          attachmentMeta,
        );
      }
    } else if (newMediaAttachment) {
      // Case 3: Add or replace with media attachment
      const { file, attachmentId, thumbnailId } = newMediaAttachment;

      // Blurred placeholder + original dimensions for the receiver's bubble.
      // Best-effort: any failure just omits the field (never blocks the send).
      const previewMeta = await getMediaPreviewMeta(
        file,
        newMediaAttachment.thumbnailBlob,
        getStagedVideoDims(attachmentId),
      );

      // Create attachment metadata
      attachmentMeta = {
        attachmentId,
        attachmentThumbnailId: thumbnailId,
        filename: file.name,
        fileSize: file.size,
        fileType: file.type,
        ...previewMeta,
      };

      // Upload the new full-size attachment under the send cap (see file path).
      await runBlobSend(
        async () => {
          const encryptedAttachment = await encryptMessageAttachment(
            chatDetails.eCSK,
            chatDetails.currentEpoch ?? 0,
            chatId,
            file,
          );
          await postAttachment({
            attachment_id: attachmentId,
            filename: file.name,
            chat_id: chatId,
            encryptedAttachment,
          });
        },
        { sizeBytes: file.size },
      );

      // Upload the thumbnail (createThumbnail produces WebP via canvas.toBlob)
      const thumbnailFile = new File(
        [newMediaAttachment.thumbnailBlob],
        "thumbnail",
        {
          type: "image/webp",
        },
      );
      const encryptedThumbnail = await encryptMessageAttachment(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        thumbnailFile,
      );
      await postAttachment({
        attachment_id: thumbnailId,
        filename: "thumbnail",
        chat_id: chatId,
        encryptedAttachment: encryptedThumbnail,
      });

      // Add to local DB
      await addMediaAttachmentToLocalDB({
        id: attachmentId,
        chatId,
        messageId: message.id,
        timestamp: message.timestamp,
        content: file,
        thumbnail: newMediaAttachment.thumbnailBlob,
        fileType: file.type,
        filename: file.name,
      });

      // Encrypt attachment metadata
      encryptedAttachmentMeta = await encryptMessageAttachmentMeta(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        attachmentMeta,
      );
    } else if (newFileAttachment) {
      // Case 4: Add or replace with file attachment
      const { file, attachmentId } = newFileAttachment;

      // Create attachment metadata
      attachmentMeta = {
        attachmentId,
        attachmentThumbnailId: "", // No thumbnail for files
        filename: file.name,
        fileSize: file.size,
        fileType: file.type,
      };

      // Upload the new attachment under the send cap (see file-send path).
      await runBlobSend(
        async () => {
          const encryptedAttachment = await encryptMessageAttachment(
            chatDetails.eCSK,
            chatDetails.currentEpoch ?? 0,
            chatId,
            file,
          );
          await postAttachment({
            attachment_id: attachmentId,
            filename: file.name,
            chat_id: chatId,
            encryptedAttachment,
          });
        },
        { sizeBytes: file.size },
      );

      // Add to local DB
      await addFileAttachmentToLocalDB({
        id: attachmentId,
        chatId,
        messageId: message.id,
        timestamp: message.timestamp,
        content: file,
        fileType: file.type,
        filename: file.name,
        fileSize: file.size,
      });

      // Encrypt attachment metadata
      encryptedAttachmentMeta = await encryptMessageAttachmentMeta(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        attachmentMeta,
      );
    }

    // build NEW msg entity for optimistic update in local db
    const newMessageEntity = {
      ...message,
      content: {
        ...message.content,
        text: newContent,
        // Update attachment fields based on operation
        media:
          attachmentMeta?.fileType?.startsWith("image/") ||
          attachmentMeta?.fileType?.startsWith("video/")
            ? attachmentMeta
            : undefined,
        file:
          attachmentMeta &&
          !attachmentMeta.fileType?.startsWith("image/") &&
          !attachmentMeta.fileType?.startsWith("video/")
            ? attachmentMeta
            : undefined,
        // link: linkMessageMetadata?.url,
      },
      hasAttachment: !!attachmentMeta,
      isEdited: true,
    };

    await updateMessageByServerIdInLocalDB(message.id, newMessageEntity);
    // we update it on socket ev
    // await updateChatInLocalDB(chatId, {
    //   lastMessage: newMessageEntity,
    // });

    // SERVER STUFF

    const { encryptedContent: newEncryptedContent, key_epoch: editKeyEpoch } =
      await encryptMessageContent(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        newContent,
      );

    // TO DO => TEMPORARY
    // get the message from server so we can send it back to the PATCH request
    const eMessage = await getMessage({
      message_id: message.id,
      chat_id: message.chatId,
      category: message.category,
    });

    const encryptedMessage: EncryptedMessage = {
      ...eMessage,
      content: newEncryptedContent,
      key_epoch: editKeyEpoch,
      attachment_meta: encryptedAttachmentMeta || "",
      is_edited: true,
    };

    const messageType =
      chatDetails.type === ChatType.GROUP
        ? MessageType.GROUP
        : chatDetails.type === ChatType.P2P
          ? MessageType.P2P
          : MessageType.NOTE;

    await patchMessage(messageType, encryptedMessage);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // revert to initgial message
    await updateMessageByServerIdInLocalDB(message.id, message);
    // revert to initial chat last message
    await updateChatInLocalDB(chatId, {
      lastMessage: message,
    });
    // Preserve the original failure as `cause` so telemetry (AutoErrorReport)
    // can see WHY the edit failed instead of masking it behind a generic string.
    throw new Error("Error editing message", { cause: error });
  }
};

export const replyToMessageInChat = async ({
  chatId,
  parentMessage,
  content,
  signal,
}: {
  chatId: string;
  parentMessage: MessageEntity;
  content: string;
  signal?: AbortSignal;
}): Promise<void> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // if no payload
  if (!chatId || !content || !parentMessage) {
    return;
  }

  // if chat id is active
  const activeChatId = useNavigationState.getState().activeChatId;
  if (chatId !== activeChatId) {
    return;
  }

  const activeThreadId = useNavigationState.getState().activeThreadId;

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  const _clientId = generateUuid7();
  const now = extractTimestampFromUUID7(_clientId);

  try {
    // check if chat exists in local db
    const chatDetails = await getChatFromLocalDB(chatId);
    if (chatId !== chatDetails?.id) {
      return;
    }

    // check if existing link + metadata saved in rtf state
    // const linkMessage = useRtfInputState.getState().linkMessages[chatId];
    // const linkMessageMetadata =
    //   useRtfInputState.getState().linkMessagesMetadata[chatId];
    // const clearLinkMessageMetadata =
    //   useRtfInputState.getState().clearLinkMessageMetadata;

    // OPTIMISTYIC STUFF
    const messageEntity: MessageEntity = {
      _clientId, // Local-only ID for tracking the optimistic message
      id: _clientId,
      chatId,
      sender: userFingerPrint.username,
      avatarId: getAvatarId(userFingerPrint.username),
      timestamp: now,
      category: activeThreadId
        ? MessageCategory.COMMENT
        : MessageCategory.MESSAGE,
      content: {
        text: content,
        // link: linkMessageMetadata.url,
      },
      hasAttachment: false,
      isEdited: false,
      isOutgoing: true,
      // comment related
      commentCount: null,
      senderAvatar: getAvatarId(userFingerPrint.username),
      parentCommentCount: null,
      // TO DO => Modify function to accept thread id if replying in thread
      threadId: activeThreadId,
      commentSentAt: null, // No longer needed with UUID7
      // TO DO => modify to differentiate between reply and thread reply
      isReply: true,
      isPinned: false,
      parentMessage: {
        id: parentMessage.id,
        category: parentMessage.category,
      },
      sendingStatus: MessageSendingStatus.PENDING,
    };

    // link processing
    // if (has_links) {
    //   const processedLink: LinkEntity = {
    //     id: newMessageId,
    //     chatId,
    //     timestamp: newMessageTimestamp,
    //     url: linkMessageMetadata.url,
    //     domainUrl: linkMessageMetadata.domainUrl,
    //     faviconUrl: linkMessageMetadata.faviconUrl,
    //     metaTitle: linkMessageMetadata.title,
    //     metaDescription: linkMessageMetadata.description,
    //     ogImage: linkMessageMetadata.image,
    //     withMetadata: true,
    //   };

    //   await addLinkToLocalDB(processedLink);
    //   clearLinkMessageMetadata(chatId);
    // }

    await addMessageToLocalDB(messageEntity);
    // we update on socket ev
    // await updateChatInLocalDB(chatId, {
    //   lastMessage: messageEntity,
    //   lastMessageTimestamp: newMessageTimestamp,
    // });

    // SERVER STUFF
    const { encryptedContent: replyEncContent, key_epoch: replyKeyEpoch } =
      await encryptMessageContent(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        content,
      );

    const encryptedMessage: Partial<EncryptedMessage> = {
      client_id: _clientId, // Pass the client-generated ID to the server for tracking
      chat_id: chatId,
      sender: userFingerPrint.username,
      content: replyEncContent,
      key_epoch: replyKeyEpoch,
      is_edited: false,
      category: activeThreadId
        ? MessageCategory.COMMENT
        : MessageCategory.MESSAGE,
      parent_id: parentMessage.id,
      parent_category: activeThreadId
        ? MessageCategory.COMMENT // actually comment replies
        : MessageCategory.MESSAGE,
      attachment_meta: "",
      // thread related
      comment_count: null,
      // TO DO => Modify function to accept thread id if replying in thread
      thread_id: activeThreadId,
    };

    const messageType =
      chatDetails.type === ChatType.GROUP
        ? MessageType.GROUP
        : chatDetails.type === ChatType.P2P
          ? MessageType.P2P
          : MessageType.NOTE;

    const serverMessageIds = await postMessage(messageType, encryptedMessage);

    const serverMessageId = serverMessageIds[0];
    const serverMessageTimestamp = extractTimestampFromUUID7(serverMessageId);

    // check for race condition where message status is already "DELIVERED" or "READ" by socket event before we get response from server
    const localDbMessage = await getMessageByClientIdFromLocalDB(_clientId);
    if (
      localDbMessage &&
      (localDbMessage.sendingStatus === MessageSendingStatus.DELIVERED ||
        localDbMessage.sendingStatus === MessageSendingStatus.READ)
    ) {
      // if msg is already DELIVERED or READ, we  skip the update to SENT status
      return;
    }

    // update local db with new server message id and timestamp
    await updateMessageByServerIdInLocalDB(_clientId, {
      id: serverMessageId,
      timestamp: serverMessageTimestamp,
      sendingStatus: MessageSendingStatus.SENT,
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // TO DO => check if we have to remove any links from db
    // Update by _clientId (the immutable primary key) rather than the mutable
    // `id` index: once a message publishes, its `id` may be stamped to the
    // server id, so an index lookup keyed on _clientId would match nothing and
    // silently leave the row stuck PENDING instead of FAILED.
    await markSendFailed(_clientId);
    // Preserve the original failure as `cause` so telemetry (AutoErrorReport)
    // can see WHY the send failed (encryption, chunked upload, API, socket, …),
    // instead of collapsing every distinct failure into one opaque cluster.
    throw new Error("Error sending message", { cause: error });
  }
};

/**
 * Posts a new message with a media attachment to a chat
 * Supports both regular messages and replies
 *
 * @param chatId - ID of the chat to post to
 * @param file - Media file to attach
 * @param newAttachmentId - ID for the new attachment
 * @param newThumbnailId - ID for the thumbnail
 * @param thumbnailBlob - Thumbnail image blob
 * @param content - Optional text content
 * @param parentMessage - Optional parent message for replies
 * @param signal - Optional abort signal
 */
export const postNewMessageWithMediaAttachmentToChat = async ({
  chatId,
  file,
  newAttachmentId,
  newThumbnailId,
  thumbnailBlob,
  content,
  parentMessage,
  signal,
  skipSendQueue,
}: {
  chatId: string;
  file: File;
  newAttachmentId: string;
  newThumbnailId: string;
  thumbnailBlob: Blob;
  content: string; //  TO DO => check if optional as user can send only attachment
  parentMessage?: MessageEntity;
  signal?: AbortSignal;
  // Bypass the send cap (new-AI-chat first send only — see the file path).
  skipSendQueue?: boolean;
}): Promise<void> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // if no payload
  if (
    !chatId ||
    !newAttachmentId ||
    !newThumbnailId ||
    !file ||
    !thumbnailBlob
  ) {
    return;
  }

  // Send targets an explicit chatId, but the rest of this flow reads live
  // navigation state (thread id, etc.), so it can only build a consistent
  // message for the active chat. If the user navigated away between composing
  // and this call, surface it instead of silently dropping the message — the
  // caller already cleared the input + attachment, so a silent return would
  // lose it with nothing to retry. The caller's catch shows a toast.
  const activeChatId = useNavigationState.getState().activeChatId;
  if (chatId !== activeChatId) {
    throw new Error(
      "Message not sent — the active chat changed. Please try again.",
    );
  }

  const activeThreadId = useNavigationState.getState().activeThreadId;

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  const _clientId = generateUuid7();
  const now = extractTimestampFromUUID7(_clientId);

  let chunkedUploadData:
    | {
        uploadId: string;
        csk: string;
        uploadToken: string;
        controller: AbortController;
      }
    | undefined;
  // Once the message is published, receivers may already be streaming the parts,
  // so a failure must NOT delete them server-side (it would 404 a receiver
  // mid-stream). Tracks that boundary for the catch blocks.
  let messagePublished = false;

  // check if chat exists in local db
  const chatDetails = await getChatFromLocalDB(chatId);
  if (chatId !== chatDetails?.id) {
    return;
  }

  // Authoritative size check, resolved against the DESTINATION chat rather than
  // whichever composer was open when the file was picked — see the equivalent
  // check in postNewMessageWithFileAttachmentToChat for what it guards against
  // and why it is outside the try. Video resolves per scope: the regular
  // per-plan ladder (clamped to MAX_VIDEO_FILE_SIZE) in a normal chat, and
  // BLOCKED in an Architect chat (no matrix cell), so a video addressed to an
  // Architect destination is refused here too.
  //
  // Running it here also means a rejection happens before any chunked upload
  // starts, so there is nothing to tear down.
  const sizeRejection = attachmentSizeRejection(file.size, {
    fileType: file.type,
    isArchitect: isArchitectChat(chatDetails.type, chatDetails.hubId),
    plan: useSubscriptionState.getState().subscription.plan,
  });
  if (sizeRejection) {
    throw new Error(sizeRejection);
  }

  // TO DO => PREVENT DOUBLE updates in LOCAL DB (middleware/socket events) -
  try {
    // ................
    // OPTIMISTIC STUFF
    // ................

    // For large videos, start the upload now to get the uploadId before building metadata.
    // This lets us use uploadId as the attachmentId throughout (no need for a later correction).
    const isChunkedVideo =
      featureFlags.chunkedUpload &&
      file.type.startsWith("video/") &&
      file.size > LARGE_ATTACHMENT_THRESHOLD;
    if (isChunkedVideo) {
      const csk = await getOrDecapsulateCSK({
        username: userFingerPrint.username,
        dk: userFingerPrint.dk,
        ePrK: userFingerPrint.ePrK,
        eCSK: chatDetails.eCSK,
      });
      // Hold a cross-tab upload slot (Web Locks) BEFORE the startUpload
      // round-trip, so a rapid second send counts this session immediately.
      // Acquiring only at register — after startUpload resolves — left a
      // window where an in-flight session wasn't counted yet, so back-to-back
      // sends on a slow network could slip past the cap. Released at
      // unregister on every settle path, by the browser if this tab dies, or
      // right here if startUpload itself fails (so a failed start can't leak a
      // slot). A null grant = another tab won a simultaneous-send race at the
      // cap boundary: proceed anyway (transient over-cap by one, erased by the
      // server-side 429 backstop). The gate→acquire split stays best-effort by
      // design; the server backstop is the real global enforcement.
      const releaseSlot = (await acquireUploadSlot()) ?? undefined;
      let uploadId: string;
      let uploadToken: string;
      try {
        ({ uploadId, uploadToken } = await startUpload(
          chatId,
          file.name,
          file.type,
          file.size,
          CHUNK_SIZE,
        ));
      } catch (err) {
        releaseSlot?.();
        throw err;
      }
      // Per-upload AbortController so the user can cancel from the progress ring.
      // Forward any externally-passed signal into it too.
      const controller = new AbortController();
      if (signal) {
        if (signal.aborted) {
          controller.abort();
        } else {
          signal.addEventListener("abort", () => controller.abort(), {
            once: true,
          });
        }
      }
      chunkedUploadData = { uploadId, csk, uploadToken, controller };
      useUploadProgressState.getState().setProgress(uploadId, 0);
      // Register the cancel trigger + the original file so the sender can (a)
      // cancel via the ring's center button and (b) preview the video locally
      // while it's still uploading. Cleared when the upload settles (which also
      // releases the slot acquired above).
      useUploadControlState.getState().register(uploadId, {
        cancel: () => controller.abort(),
        file,
        releaseSlot,
      });
    }

    // build the attachment meta. For chunked video, mark it "uploading" from the
    // start so BOTH the sender's optimistic row and the receiver's initial create
    // carry the streaming state — without it, opening the video before completion
    // registers no uploadMeta and hits the (not-yet-assembled) endpoint too early.
    // Blurred placeholder + original dimensions for the receiver's bubble.
    // Best-effort: any failure just omits the field (never blocks the send).
    // Embedded in the optimistic meta below so the sender's own row carries it too.
    const previewMeta = await getMediaPreviewMeta(
      file,
      thumbnailBlob,
      getStagedVideoDims(newAttachmentId),
    );

    const mediaAttachmentMeta: AttachmentMeta = {
      attachmentId: chunkedUploadData?.uploadId ?? newAttachmentId,
      attachmentThumbnailId: newThumbnailId,
      filename: file.name,
      fileSize: file.size,
      fileType: file.type,
      isLargeAttachment: isChunkedVideo,
      ...previewMeta,
      ...(isChunkedVideo
        ? { uploadState: { status: "uploading" as const } }
        : {}),
    };

    // build msg entity for optimistic update in local db - Messages Collection
    const messageEntity: MessageEntity = {
      _clientId, // Local-only ID for tracking the optimistic message
      id: _clientId,
      chatId,
      sender: userFingerPrint.username,
      avatarId: getAvatarId(userFingerPrint.username),
      timestamp: now,
      category: activeThreadId
        ? MessageCategory.COMMENT
        : MessageCategory.MESSAGE,
      content: {
        text: content,
        media: mediaAttachmentMeta,
      },
      hasAttachment: true,
      isEdited: false,
      isOutgoing: true,
      // comment related
      commentCount: null,
      senderAvatar: getAvatarId(userFingerPrint.username),
      parentCommentCount: null,
      // TO DO => Modify function to accept thread id if replying in thread
      threadId: activeThreadId,
      commentSentAt: null, // No longer needed with UUID7
      // reply related
      isReply: true,
      isPinned: false,
      parentMessage: parentMessage
        ? {
            id: parentMessage.id,
            category: parentMessage.category,
          }
        : undefined,
      sendingStatus: MessageSendingStatus.PENDING,
    };

    // build media attachment entity for optimistic update in local db - media attachments Collection
    const mediaAttachmentEntity: MediaAttachmentEntity = {
      id: chunkedUploadData?.uploadId ?? newAttachmentId,
      chatId,
      messageId: _clientId,
      timestamp: now,
      thumbnail: thumbnailBlob,
      // large videos are streamed from the server — skip storing the full blob locally
      ...(isChunkedVideo ? {} : { content: file }),
      fileType: file.type,
      filename: file.name,
      isLargeAttachment: isChunkedVideo,
    };

    await addMediaAttachmentToLocalDB(mediaAttachmentEntity);
    await addMessageToLocalDB(messageEntity);

    // ................
    // SERVER  STUFF
    // ................

    const { encryptedContent: mediaEncContent, key_epoch: mediaKeyEpoch } =
      await encryptMessageContent(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        content,
      );

    // encrypt and upload thumbnail (WebP via canvas.toBlob)
    const _thumbnail = new File([thumbnailBlob], "thumbnail", {
      type: "image/webp",
    });
    const encryptedThumbnail = await encryptMessageAttachment(
      chatDetails.eCSK,
      chatDetails.currentEpoch ?? 0,
      chatId,
      _thumbnail,
    );
    await postAttachment({
      attachment_id: newThumbnailId,
      filename: "thumbnail",
      chat_id: chatId,
      encryptedAttachment: encryptedThumbnail,
    });

    const messageType =
      chatDetails.type === ChatType.GROUP
        ? MessageType.GROUP
        : chatDetails.type === ChatType.P2P
          ? MessageType.P2P
          : MessageType.NOTE;

    if (chunkedUploadData) {
      // ── PROGRESSIVE CHUNKED UPLOAD ─────────────────────────────────────────
      // Send the message NOW (thumbnail + uploadId already available) so the
      // receiver sees it immediately with a live progress indicator while the
      // rest of the file uploads in the background.
      const { uploadId, csk, uploadToken, controller } = chunkedUploadData;
      const uploadSignal = controller.signal;
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);

      const initialUploadState: UploadState = {
        status: "uploading",
      };
      const initialAttachmentMeta: AttachmentMeta = {
        ...mediaAttachmentMeta,
        uploadState: initialUploadState,
      };
      const encryptedAttachmentMeta = await encryptMessageAttachmentMeta(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        initialAttachmentMeta,
      );

      const encryptedMessage: Partial<EncryptedMessage> = {
        client_id: _clientId,
        chat_id: chatId,
        sender: userFingerPrint.username,
        content: mediaEncContent,
        key_epoch: mediaKeyEpoch,
        is_edited: false,
        category: activeThreadId
          ? MessageCategory.COMMENT
          : MessageCategory.MESSAGE,
        comment_count: null,
        thread_id: activeThreadId,
        parent_id: parentMessage?.id || undefined,
        parent_category: parentMessage?.category || undefined,
        attachment_meta: encryptedAttachmentMeta,
      };

      // Pre-publish cancel: the user hit cancel before we published. Drop the
      // optimistic row FIRST so it disappears instantly, then reclaim the
      // (empty/partial) upload. There's nothing to delete server-side since the
      // message was never sent.
      if (uploadSignal.aborted) {
        useUploadProgressState.getState().clearProgress(uploadId);
        await removeMessageByClientIdFromLocalDB(_clientId);
        await removeMediaAttachmentFromLocalDB(uploadId);
        useUploadControlState.getState().unregister(uploadId);
        await abortUpload(uploadId, uploadToken).catch(() => {});
        return;
      }

      // Send early — receiver sees thumbnail + "Uploading 0%" immediately
      const serverMessageIds = await postMessage(messageType, encryptedMessage);
      const serverMessageId = serverMessageIds[0];
      const serverMessageTimestamp = extractTimestampFromUUID7(serverMessageId);
      messagePublished = true;

      // Stamp the server id onto the optimistic row NOW (status stays PENDING),
      // before the multi-minute upload loop. Reconciliation paths (socket CREATE
      // echo, new-sync) key on the server id; if we left id=_clientId for the
      // whole upload window, an echo/sync arriving mid-upload would fail to find
      // this row and insert a DUPLICATE (or the later status update would no-op).
      // Looked up by _clientId (the immutable primary key), so it's race-proof.
      await updateMessageByClientIdInLocalDB(_clientId, {
        id: serverMessageId,
        timestamp: serverMessageTimestamp,
      });

      // Patches only the attachment_meta field so the receiver's SW knows the
      // upload phase changed.  Retries up to 3 times; swallowed on final
      // failure — must not abort the upload.
      const patchUploadStatus = async (
        status: UploadState["status"],
      ): Promise<void> => {
        const updatedMeta: AttachmentMeta = {
          ...mediaAttachmentMeta,
          uploadState: { status },
        };
        const newEncryptedMeta = await encryptMessageAttachmentMeta(
          chatDetails.eCSK,
          chatDetails.currentEpoch ?? 0,
          chatId,
          updatedMeta,
        );
        const payload = {
          id: serverMessageId,
          chat_id: chatId,
          sender: userFingerPrint.username,
          content: mediaEncContent,
          attachment_meta: newEncryptedMeta,
          is_edited: false,
        };
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            await patchMessage(messageType, payload);
            return;
          } catch {
            if (attempt < 2) {
              await new Promise((r) => setTimeout(r, 1000 * (attempt + 1)));
            }
          }
        }
        // All retries exhausted. We do NOT fail the upload over a status patch,
        // but the loss is not silent: the receiver's persisted uploadState stays
        // "uploading" until they reload (no SW uploadMeta registered on a fresh
        // load → assembled endpoint → plays). Surface it for observability.
        // RESIDUAL (H2): durable delivery of the terminal status (outbox/retry)
        // is a tracked follow-up — see docs/chunked-video-upload.md
        // (Part IV, Deferred: durable status outbox).
        console.warn(
          `[chunked-upload] failed to deliver "${status}" status patch for ${uploadId} after 3 attempts`,
        );
      };

      const { setProgress, clearProgress } = useUploadProgressState.getState();
      const parts: { PartNumber: number }[] = [];
      // Upload order: for non-faststart MP4s (moov index at the end of the
      // file) send part 1 (file header) and the moov-bearing tail parts FIRST,
      // then the rest ascending — a receiver's <video> probes head-then-index
      // before it can start playback, so this enables watch-while-uploading
      // instead of gating the first frame on the very last part. Chunks are
      // independent AES-GCM messages and the backend stores parts as
      // individually-addressable objects (order unconstrained; the part list
      // is validated at complete), so reordering is safe end-to-end. Falls
      // back to sequential for faststart/non-MP4/unparseable files.
      const uploadOrder = computeChunkUploadOrder(
        file.size,
        CHUNK_SIZE,
        await findMoovRange(file),
      );
      try {
        for (const partNumber of uploadOrder) {
          if (uploadSignal.aborted) {
            throw new Error("Upload aborted");
          }
          const chunk = await encryptFileChunk(
            file,
            csk,
            partNumber - 1,
            CHUNK_SIZE,
          );
          await runUploadCallWithRetry(
            () =>
              uploadPart(
                uploadId,
                partNumber,
                chunk,
                uploadToken,
                uploadSignal,
              ),
            uploadSignal,
          );
          parts.push({ PartNumber: partNumber });
          setProgress(uploadId, Math.round((parts.length / totalChunks) * 100));
        }

        // Retry the finish-line call through a transient 5xx / network blip
        // (e.g. a GCS compose/metadata hiccup) so we don't lose a large upload
        // on its last step. A 4xx (invalid part list / authorization) is
        // terminal. Idempotent server-side, so re-composing is safe.
        await runUploadCallWithRetry(
          () => completeUpload(uploadId, parts, uploadToken),
          uploadSignal,
        );
        clearProgress(uploadId);
        // Finalizing — disable cancel so a click in the completion race is a
        // harmless no-op (the ring/X is already gone via clearProgress).
        useUploadControlState.getState().unregister(uploadId);

        // Persist the sender's own copy locally so previewing the just-sent
        // video is a cache hit instead of a redundant re-download+decrypt from
        // the server, and flip the local uploadState to "complete" so the
        // attachment viewer stops routing this (now-assembled) file through the
        // in-progress streaming endpoint. Best-effort: the message already sent
        // successfully, so a local storage hiccup must not fail the send.
        try {
          // Write the terminal "complete" uploadState FIRST (a fast row update),
          // THEN cache the local file copy (a large OPFS write). If the tab dies
          // during the big file write, the row is already "complete" so the
          // stranded-send reconciliation recovers it to SENT instead of wrongly
          // FAILing an already-delivered upload. The local copy is best-effort
          // preview caching — missing it only costs a one-off re-download.
          await updateMessageByClientIdInLocalDB(_clientId, {
            content: {
              text: content,
              media: {
                ...mediaAttachmentMeta,
                uploadState: { status: "complete" },
              },
            },
          });
          await updateMediaAttachmentContent(uploadId, "content", file);
        } catch (persistErr) {
          // eslint-disable-next-line no-console
          console.warn(
            `[chunked-upload] failed to persist local copy for ${uploadId}`,
            persistErr,
          );
        }

        // Notify receivers that upload is done — SW switches to assembled endpoint
        await patchUploadStatus("complete");
      } catch (uploadErr) {
        clearProgress(uploadId);

        if (uploadSignal.aborted) {
          // ── USER CANCEL (post-publish) ──────────────────────────────────
          // Remove the message locally FIRST so the bubble disappears instantly
          // on click — don't make the user wait on two network round-trips.
          // Snapshot it so we can restore it if the server delete fails.
          const cancelledMessage =
            await getMessageByClientIdFromLocalDB(_clientId);
          await removeMessageByClientIdFromLocalDB(_clientId);
          useUploadControlState.getState().unregister(uploadId);

          // Server cleanup, in the backend-mandated order: delete (Rust) first,
          // and only on success reclaim the parts (Umbra `abortUpload`). The two
          // live on separate services with no shared transaction, so a failed
          // delete must NOT reclaim parts (keep them for a retry) — and
          // delete-first also shrinks the recipient's interim-404 window. The
          // recipient's Delete event (Rust broker) removes it on their side and
          // the socket delete handler stops their service worker.
          try {
            await deleteMessage({
              message_id: serverMessageId,
              chat_id: chatId,
              category: activeThreadId
                ? MessageCategory.COMMENT
                : MessageCategory.MESSAGE,
              message_type: messageType,
            });
          } catch (deleteErr) {
            // Delete failed — restore the message locally as FAILED so nothing
            // is silently lost, and do NOT abort (parts stay intact for a retry).
            if (cancelledMessage) {
              await addMessageToLocalDB({
                ...cancelledMessage,
                sendingStatus: MessageSendingStatus.FAILED,
              });
            }
            // eslint-disable-next-line no-console
            console.warn(
              `[chunked-upload] cancel: message delete failed for ${uploadId}; restored as FAILED`,
              deleteErr,
            );
            return;
          }
          // Delete succeeded → reclaim the temp parts (best-effort) and purge the
          // local media/thumbnail.
          await abortUpload(uploadId, uploadToken).catch(() => {});
          await removeMediaAttachmentFromLocalDB(uploadId);
          return; // deliberate cancel — no throw, no error toast
        }

        // ── UPLOAD FAILURE (not a cancel) ─────────────────────────────────
        // Tell receivers the upload failed so their SW stops streaming and shows
        // an error promptly (instead of polling deleted parts for 90s).
        await patchUploadStatus("failed");
        // Do NOT abortUpload here: the message is already published and receivers
        // may be mid-stream on these parts. Deleting them server-side would 404 a
        // receiver. Leave the temp parts for the GCS lifecycle to reclaim.
        // Update by _clientId (immutable PK) — the row's `id` is now serverMessageId
        // and may have been further reconciled by a socket echo.
        await updateMessageByClientIdInLocalDB(_clientId, {
          sendingStatus: MessageSendingStatus.FAILED,
        });
        useUploadControlState.getState().unregister(uploadId);
        throw uploadErr;
      }

      // Check race condition: socket may have already marked DELIVERED/READ
      const localDbMessage = await getMessageByClientIdFromLocalDB(_clientId);
      if (
        localDbMessage &&
        (localDbMessage.sendingStatus === MessageSendingStatus.DELIVERED ||
          localDbMessage.sendingStatus === MessageSendingStatus.READ)
      ) {
        return;
      }

      // id + timestamp were stamped right after publish; only the status remains.
      // By _clientId (immutable PK) so it can't no-op after a mid-upload reconcile.
      await updateMessageByClientIdInLocalDB(_clientId, {
        sendingStatus: MessageSendingStatus.SENT,
      });
    } else {
      // ── STANDARD UPLOAD (images / small videos) ────────────────────────────
      // TO DO => check if extraction to middleware function needed
      const encryptedAttachmentMeta = (
        await encryptMessageContent(
          chatDetails.eCSK,
          chatDetails.currentEpoch ?? 0,
          chatId,
          JSON.stringify(mediaAttachmentMeta),
        )
      ).encryptedContent;

      // Encrypt + upload the full-size image under the send cap (see file path).
      // Thumbnail (already uploaded above) is small and left uncapped.
      await runBlobSend(
        async () => {
          const encryptedFullSizeAttachment = await encryptMessageAttachment(
            chatDetails.eCSK,
            chatDetails.currentEpoch ?? 0,
            chatId,
            file,
          );
          await postAttachment({
            attachment_id: newAttachmentId,
            filename: file.name,
            chat_id: chatId,
            encryptedAttachment: encryptedFullSizeAttachment,
          });
        },
        { skip: skipSendQueue, sizeBytes: file.size },
      );

      const encryptedMessage: Partial<EncryptedMessage> = {
        client_id: _clientId,
        chat_id: chatId,
        sender: userFingerPrint.username,
        content: mediaEncContent,
        key_epoch: mediaKeyEpoch,
        is_edited: false,
        category: activeThreadId
          ? MessageCategory.COMMENT
          : MessageCategory.MESSAGE,
        comment_count: null,
        thread_id: activeThreadId,
        parent_id: parentMessage?.id || undefined,
        parent_category: parentMessage?.category || undefined,
        attachment_meta: encryptedAttachmentMeta,
      };

      const serverMessageIds = await postMessage(messageType, encryptedMessage);
      const serverMessageId = serverMessageIds[0];
      const serverMessageTimestamp = extractTimestampFromUUID7(serverMessageId);

      const localDbMessage = await getMessageByClientIdFromLocalDB(_clientId);
      if (
        localDbMessage &&
        (localDbMessage.sendingStatus === MessageSendingStatus.DELIVERED ||
          localDbMessage.sendingStatus === MessageSendingStatus.READ)
      ) {
        return;
      }

      await updateMessageByServerIdInLocalDB(_clientId, {
        id: serverMessageId,
        timestamp: serverMessageTimestamp,
        sendingStatus: MessageSendingStatus.SENT,
      });
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // Handles failures in: chat lookup, startUpload, thumbnail upload, or
    // errors re-thrown from the inner chunked-upload try-catch above.
    if (chunkedUploadData) {
      useUploadProgressState
        .getState()
        .clearProgress(chunkedUploadData.uploadId);
      // Drop the control entry + release the cross-tab upload slot on EVERY
      // failure path. A throw AFTER register() but before the inner upload
      // try/catch — most reachably the send-early postMessage failing — would
      // otherwise leak the Web Lock for this tab's whole lifetime. The
      // stranded-send reconciliation reads that lock CROSS-TAB, so one leak makes
      // every tab treat an upload as "live" and skip its FAILED sweeps forever.
      // Idempotent: the inner catch paths that already unregistered no-op here.
      useUploadControlState.getState().unregister(chunkedUploadData.uploadId);
      // Only delete the server-side parts when the message was never published —
      // then no receiver can be streaming them. Post-publish failures leave the
      // parts for the GCS lifecycle to reclaim (the inner catch already patched
      // the "failed" status for receivers).
      if (!messagePublished) {
        await abortUpload(
          chunkedUploadData.uploadId,
          chunkedUploadData.uploadToken,
        ).catch(() => {});
      }
    }
    // Update by _clientId (the immutable primary key) rather than the mutable
    // `id` index: once a message publishes, its `id` may be stamped to the
    // server id, so an index lookup keyed on _clientId would match nothing and
    // silently leave the row stuck PENDING instead of FAILED.
    await markSendFailed(_clientId);
    // Preserve the original failure as `cause` so telemetry (AutoErrorReport)
    // can see WHY the send failed (encryption, chunked upload, API, socket, …),
    // instead of collapsing every distinct failure into one opaque cluster.
    throw new Error("Error sending message", { cause: error });
  }
};

/**
 * Posts a new message with a file attachment to a chat
 * Supports both regular messages and replies
 *
 * @param chatId - ID of the chat to post to
 * @param file - File to attach
 * @param newAttachmentId - ID for the new attachment
 * @param content - Optional text content
 * @param parentMessage - Optional parent message for replies
 * @param signal - Optional abort signal
 */
export const postNewMessageWithFileAttachmentToChat = async ({
  chatId,
  file,
  newAttachmentId,
  content,
  parentMessage,
  signal,
  skipSendQueue,
}: {
  chatId: string;
  file: File;
  newAttachmentId: string;
  content: string; //  TO DO => check if optional as user can send only attachment
  parentMessage?: MessageEntity;
  signal?: AbortSignal;
  // Bypass the concurrent-send cap. Only the new-AI-chat first send sets this —
  // it awaits behind a full-screen loader where queueing would freeze the user.
  skipSendQueue?: boolean;
}): Promise<void> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // if no payload
  if (!chatId || !newAttachmentId || !file) {
    return;
  }

  const activeThreadId = useNavigationState.getState().activeThreadId;

  // if chat id is active
  const activeChatId = useNavigationState.getState().activeChatId;
  if (chatId !== activeChatId) {
    return;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  const _clientId = generateUuid7();
  const now = extractTimestampFromUUID7(_clientId);

  // check if chat exists in local db
  const chatDetails = await getChatFromLocalDB(chatId);
  if (chatId !== chatDetails?.id) {
    return;
  }

  // Authoritative PDF size check, resolved against the DESTINATION chat rather
  // than whichever composer was open when the file was picked.
  //
  // Staging alone cannot be trusted for this. The sender stores are
  // module-global, so a staged file is only tied to the composer that picked it
  // by convention — useRtfState discards on chat switch AND on unmount, and
  // NewAIChatForm rejects an over-cap file before creating anything. Both were
  // added because the file DID cross composers: ChatPlayground unmounts for the
  // new-AI-chat form, and before the unmount cleanup existed nothing cleared it
  // on the way out.
  //
  // This check is the backstop for that class of bug rather than any one
  // instance of it: it is the only place that reads the real destination, so it
  // still holds if the discard effect changes, if drafts become per-chat, or if
  // a send path appears that does not go through those composers.
  //
  // It resolves the cap for EVERY destination, not just Architect ones —
  // gating the check itself on isArchitectChat would leave regular chats with
  // no send-time check at all.
  //
  // Deliberately OUTSIDE the try below. Inside it, the catch would replace this
  // message with the generic "Error sending message" before the caller could
  // show it, and would write a FAILED row for a _clientId that has no row yet —
  // the optimistic write happens further down.
  const sizeRejection = attachmentSizeRejection(file.size, {
    fileType: file.type,
    isArchitect: isArchitectChat(chatDetails.type, chatDetails.hubId),
    plan: useSubscriptionState.getState().subscription.plan,
  });
  if (sizeRejection) {
    throw new Error(sizeRejection);
  }

  // TO DO => PREVENT DOUBLE updates in LOCAL DB (middleware/socket events) -
  try {
    // ................
    // OPTIMISTIC STUFF
    // ................

    // build the attachment meta
    const mediaAttachmentMeta: AttachmentMeta = {
      attachmentId: newAttachmentId,
      attachmentThumbnailId: "", // TO DO => check if thuimbnail for docs/files is needed
      filename: file.name,
      fileSize: file.size,
      fileType: file.type,
      // Documents upload as a single blob and never take the chunked/SW
      // streaming path this flag routes to (every consumer is video-gated).
      // With the 100 MB doc cap, size-derived stamping would mark most large
      // PDFs as "large" in persisted encrypted metadata — a lie a future
      // consumer could trip over.
      isLargeAttachment: false,
    };

    // build msg entity for optimistic update in local db - Messages Collection
    const messageEntity: MessageEntity = {
      _clientId: _clientId, // Local-only ID for tracking the optimistic message
      id: _clientId,
      chatId,
      sender: userFingerPrint.username,
      avatarId: getAvatarId(userFingerPrint.username),
      timestamp: now,
      category: activeThreadId
        ? MessageCategory.COMMENT
        : MessageCategory.MESSAGE,
      content: {
        text: content || "",
        file: mediaAttachmentMeta,
      },
      hasAttachment: true,
      isEdited: false,
      isOutgoing: true,
      // comment related
      commentCount: null,
      senderAvatar: getAvatarId(userFingerPrint.username),
      parentCommentCount: null,
      // TO DO => Modify function to accept thread id if replying in thread
      threadId: activeThreadId,
      commentSentAt: null, // No longer needed with UUID7
      // reply related
      isReply: true,
      isPinned: false,
      parentMessage: parentMessage
        ? {
            id: parentMessage.id,
            category: parentMessage.category,
          }
        : undefined,
      sendingStatus: MessageSendingStatus.PENDING,
    };

    // build file attachment entity for optimistic update in local db - File attachments Collection
    const fileAttachmentEntity: FileAttachmentEntity = {
      id: newAttachmentId,
      chatId,
      messageId: _clientId,
      timestamp: now,
      content: file,
      fileType: file.type,
      filename: file.name,
      fileSize: file.size,
    };

    await addFileAttachmentToLocalDB(fileAttachmentEntity);
    await addMessageToLocalDB(messageEntity);

    // ................
    // SERVER  STUFF
    // ................

    const { encryptedContent: fileEncContent, key_epoch: fileKeyEpoch } =
      await encryptMessageContent(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        content,
      );

    // // encrypt attachment meta
    // TO DO => check if exctraction to middleware function needed
    const { encryptedContent: fileEncAttachmentMeta } =
      await encryptMessageContent(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        JSON.stringify(mediaAttachmentMeta),
      );

    // Encrypt + upload under the concurrent-send cap: the encrypt holds the whole
    // file (~3x its size) transiently, so this span is where a burst of large
    // sends OOMs the tab. QUEUE, don't reject; the optimistic write above already
    // ran, so the bubble is already visible (PENDING) while this waits its turn.
    await runBlobSend(
      async () => {
        const encryptedFile = await encryptMessageAttachment(
          chatDetails.eCSK,
          chatDetails.currentEpoch ?? 0,
          chatId,
          file,
        );
        await postAttachment({
          attachment_id: newAttachmentId,
          filename: file.name,
          chat_id: chatId,
          encryptedAttachment: encryptedFile,
        });
      },
      { skip: skipSendQueue, sizeBytes: file.size },
    );

    const encryptedMessage: Partial<EncryptedMessage> = {
      // id: newMessageId,
      client_id: _clientId, // Pass the client-generated ID to the server for tracking
      chat_id: chatId,
      sender: userFingerPrint.username,
      content: fileEncContent,
      key_epoch: fileKeyEpoch,
      is_edited: false,
      category: activeThreadId
        ? MessageCategory.COMMENT
        : MessageCategory.MESSAGE,
      comment_count: null,
      thread_id: activeThreadId,
      parent_id: parentMessage?.id || undefined,
      parent_category: parentMessage?.category || undefined,
      attachment_meta: fileEncAttachmentMeta,
    };

    const messageType =
      chatDetails.type === ChatType.GROUP
        ? MessageType.GROUP
        : chatDetails.type === ChatType.P2P
          ? MessageType.P2P
          : MessageType.NOTE;

    const serverMessageIds = await postMessage(messageType, encryptedMessage);
    const serverMessageId = serverMessageIds[0];
    const serverMessageTimestamp = extractTimestampFromUUID7(serverMessageId);

    // check for race condition where message status is already "DELIVERED" or "READ" by socket event before we get response from server
    const localDbMessage = await getMessageByClientIdFromLocalDB(_clientId);
    if (
      localDbMessage &&
      (localDbMessage.sendingStatus === MessageSendingStatus.DELIVERED ||
        localDbMessage.sendingStatus === MessageSendingStatus.READ)
    ) {
      // if msg is already DELIVERED or READ, we  skip the update to SENT status
      return;
    }

    // update local db with new server message id and timestamp
    await updateMessageByServerIdInLocalDB(_clientId, {
      id: serverMessageId,
      timestamp: serverMessageTimestamp,
      sendingStatus: MessageSendingStatus.SENT,
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // TO DO => check if we have to remove anything from db
    // TO DO => check if we have to add extra status flag for attachment processing
    // Update by _clientId (the immutable primary key) rather than the mutable
    // `id` index: once a message publishes, its `id` may be stamped to the
    // server id, so an index lookup keyed on _clientId would match nothing and
    // silently leave the row stuck PENDING instead of FAILED.
    await markSendFailed(_clientId);
    // Preserve the original failure as `cause` so telemetry (AutoErrorReport)
    // can see WHY the send failed (encryption, chunked upload, API, socket, …),
    // instead of collapsing every distinct failure into one opaque cluster.
    throw new Error("Error sending message", { cause: error });
  }
};

/**
 * Posts a new message with a file attachment to a chat
 * Supports both regular messages and replies
 *
 * @param chatId - ID of the chat to post to
 * @param file - File to attach
 * @param newAttachmentId - ID for the new attachment
 * @param parentMessage - Optional parent message for replies
 * @param signal - Optional abort signal
 */
export const postNewVoiceMessageToChat = async ({
  chatId,
  file,
  newAttachmentId,
  parentMessage,
  duration,
  waveform,
  signal,
}: {
  chatId: string;
  file: File;
  newAttachmentId: string;
  parentMessage?: MessageEntity;
  duration: number;
  waveform: number[];
  signal?: AbortSignal;
}): Promise<void> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // if no payload
  if (!chatId || !file) {
    return;
  }

  const activeThreadId = useNavigationState.getState().activeThreadId;

  // if chat id is active
  const activeChatId = useNavigationState.getState().activeChatId;
  if (chatId !== activeChatId) {
    return;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  const _clientId = generateUuid7();
  const now = extractTimestampFromUUID7(_clientId);

  // TO DO => PREVENT DOUBLE updates in LOCAL DB (middleware/socket events) -
  try {
    // check if chat exists in local db
    const chatDetails = await getChatFromLocalDB(chatId);
    if (chatId !== chatDetails?.id) {
      return;
    }

    // TO DO => Check if we keep generating ids on the client for attachments (files, audio, images...etc)

    // ................
    // OPTIMISTIC STUFF
    // ................

    // build the attachment meta
    const voiceAttachmentMeta: VoiceAttachmentMeta = {
      attachmentId: newAttachmentId,
      filename: file.name,
      fileSize: file.size,
      fileType: file.type,
      duration,
      waveform,
      isLargeAttachment: file.size > 10485760, // TO DO => check what size config
    };

    // build msg entity for optimistic update in local db - Messages Collection
    const messageEntity: MessageEntity = {
      _clientId: _clientId, // Local-only ID for tracking the optimistic message
      id: _clientId,
      chatId,
      sender: userFingerPrint.username,
      avatarId: getAvatarId(userFingerPrint.username),
      timestamp: now,
      category: activeThreadId
        ? MessageCategory.COMMENT
        : MessageCategory.MESSAGE,
      content: {
        text: "",
        voice: voiceAttachmentMeta,
      },
      hasAttachment: true,
      isEdited: false,
      isOutgoing: true,
      // comment related
      commentCount: null,
      senderAvatar: getAvatarId(userFingerPrint.username),
      parentCommentCount: null,
      // TO DO => Modify function to accept thread id if replying in thread
      threadId: activeThreadId,
      commentSentAt: null, // No longer needed with UUID7
      // reply related
      isReply: !!parentMessage,
      isPinned: false,
      parentMessage: parentMessage
        ? {
            id: parentMessage.id,
            category: parentMessage.category,
          }
        : undefined,
      sendingStatus: MessageSendingStatus.PENDING,
    };

    // build file attachment entity for optimistic update in local db - File attachments Collection
    const audioAttachmentEntity: VoiceAttachmentEntity = {
      id: newAttachmentId,
      chatId,
      messageId: _clientId,
      timestamp: now,
      content: file,
      fileType: file.type,
      filename: file.name,
    };

    await addVoiceAttachmentToLocalDB(audioAttachmentEntity);
    await addMessageToLocalDB(messageEntity);

    // ................
    // SERVER  STUFF
    // ................

    // // encrypt attachment meta
    // TO DO => check if exctraction to middleware function needed
    const {
      encryptedContent: voiceEncAttachmentMeta,
      key_epoch: voiceKeyEpoch,
    } = await encryptMessageContent(
      chatDetails.eCSK,
      chatDetails.currentEpoch ?? 0,
      chatId,
      JSON.stringify(voiceAttachmentMeta),
    );

    // Encrypt + upload under the concurrent-send cap (see the file-send path).
    // Voice notes are small, so the size gate usually lets them skip the cap.
    await runBlobSend(
      async () => {
        const encryptedFile = await encryptMessageAttachment(
          chatDetails.eCSK,
          chatDetails.currentEpoch ?? 0,
          chatId,
          file,
        );
        await postAttachment({
          attachment_id: newAttachmentId,
          filename: file.name,
          chat_id: chatId,
          encryptedAttachment: encryptedFile,
        });
      },
      { sizeBytes: file.size },
    );

    const encryptedMessage: Partial<EncryptedMessage> = {
      // id: newMessageId,
      client_id: _clientId, // Pass the client-generated ID to the server for tracking
      chat_id: chatId,
      sender: userFingerPrint.username,
      content: "", // TO DO => check if content is needed for voice messages or if we can send empty string
      key_epoch: voiceKeyEpoch,
      is_edited: false,
      category: activeThreadId
        ? MessageCategory.COMMENT
        : MessageCategory.MESSAGE,
      comment_count: null,
      thread_id: activeThreadId,
      parent_id: parentMessage?.id || undefined,
      parent_category: parentMessage?.category || undefined,
      attachment_meta: voiceEncAttachmentMeta,
    };

    const messageType =
      chatDetails.type === ChatType.GROUP
        ? MessageType.GROUP
        : chatDetails.type === ChatType.P2P
          ? MessageType.P2P
          : MessageType.NOTE;

    const serverMessageIds = await postMessage(messageType, encryptedMessage);
    const serverMessageId = serverMessageIds[0];
    const serverMessageTimestamp = extractTimestampFromUUID7(serverMessageId);

    // check for race condition where message status is already "DELIVERED" or "READ" by socket event before we get response from server
    const localDbMessage = await getMessageByClientIdFromLocalDB(_clientId);
    if (
      localDbMessage &&
      (localDbMessage.sendingStatus === MessageSendingStatus.DELIVERED ||
        localDbMessage.sendingStatus === MessageSendingStatus.READ)
    ) {
      // if msg is already DELIVERED or READ, we  skip the update to SENT status
      return;
    }

    // update local db with new server message id and timestamp
    await updateMessageByServerIdInLocalDB(_clientId, {
      id: serverMessageId,
      timestamp: serverMessageTimestamp,
      sendingStatus: MessageSendingStatus.SENT,
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // TO DO => check if we have to remove anything from db
    // TO DO => check if we have to add extra status flag for attachment processing
    // Update by _clientId (the immutable primary key) rather than the mutable
    // `id` index: once a message publishes, its `id` may be stamped to the
    // server id, so an index lookup keyed on _clientId would match nothing and
    // silently leave the row stuck PENDING instead of FAILED.
    await markSendFailed(_clientId);
    // Preserve the original failure as `cause` so telemetry (AutoErrorReport)
    // can see WHY the send failed (encryption, chunked upload, API, socket, …),
    // instead of collapsing every distinct failure into one opaque cluster.
    throw new Error("Error sending message", { cause: error });
  }
};

/**
 * Forwards an existing message to a different chat.
 *
 * How it works:
 * 1. Copies the source message's content and attachment meta
 * 2. If the message has attachments, downloads the blobs, decrypts them with
 *    the SOURCE chat's key, re-encrypts with the TARGET chat's key, and
 *    uploads under new attachment IDs
 * 3. Re-encrypts both content and attachment meta with the TARGET chat's eCSK
 * 4. Optimistically adds the message to local DB
 * 5. Posts to server
 * 6. Updates local DB with the server-assigned message ID
 */
export const forwardMessageToChat = async ({
  chatId,
  messageToForward,
  signal,
}: {
  chatId: string;
  messageToForward: MessageEntity;
  signal?: AbortSignal;
}): Promise<void> => {
  if (signal?.aborted) {
    return;
  }

  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  if (!chatId || !messageToForward) {
    return;
  }

  const chatDetails = await getChatFromLocalDB(chatId);
  if (chatId !== chatDetails?.id) {
    return;
  }

  const _clientId = generateUuid7();
  const now = extractTimestampFromUUID7(_clientId);

  const { content } = messageToForward;

  const rawAttachmentMeta =
    content.media ?? content.file ?? content.voice ?? null;

  try {
    // ................
    // OPTIMISTIC INSERT (before attachment work so catch can mark it FAILED)
    // ................

    const messageEntity: MessageEntity = {
      _clientId,
      id: _clientId,
      chatId,
      sender: userFingerPrint.username,
      avatarId: getAvatarId(userFingerPrint.username),
      timestamp: now,
      category: MessageCategory.MESSAGE,
      content: {
        text: content.text || "",
        // isLargeAttachment/uploadState reset — they describe the SOURCE's
        // chunked-upload session; the forward re-uploads as one plain object
        // (see updatedAttachmentMeta below for the server-side twin).
        ...(content.media && {
          media: {
            ...content.media,
            isLargeAttachment: false,
            uploadState: undefined,
          },
        }),
        ...(content.file && { file: { ...content.file } }),
        ...(content.voice && { voice: { ...content.voice } }),
        ...(content.link && { link: content.link }),
      },
      hasAttachment: !!rawAttachmentMeta,
      isEdited: false,
      isOutgoing: true,
      commentCount: null,
      senderAvatar: getAvatarId(userFingerPrint.username),
      parentCommentCount: null,
      threadId: null,
      commentSentAt: null,
      isReply: false,
      isPinned: false,
      sendingStatus: MessageSendingStatus.PENDING,
    };

    await addMessageToLocalDB(messageEntity);

    // ................
    // RE-ENCRYPT ATTACHMENT BLOBS
    // ................
    // Attachment blobs on the server are encrypted with the SOURCE chat's key.
    // We must download → decrypt (source key) → re-encrypt (target key) → re-upload
    // with new IDs so the target chat members can decrypt them.

    // Track the new IDs that will replace the original ones after re-encryption
    let newAttachmentId = rawAttachmentMeta?.attachmentId ?? "";
    let newThumbnailId = rawAttachmentMeta?.attachmentThumbnailId ?? "";

    if (rawAttachmentMeta) {
      // Lazily resolved source CSK — only computed if we need to download
      // from server (i.e. the blob isn't already cached locally).
      // Memoized so repeated calls (main blob + thumbnail) reuse the result.
      let sourceCSKPromise: Promise<string> | null = null;
      const getSourceCSK = () => {
        if (!sourceCSKPromise) {
          sourceCSKPromise = (async () => {
            const sourceChatDetails = await getChatFromLocalDB(
              messageToForward.chatId,
            );
            if (!sourceChatDetails) {
              throw new Error("Source chat not found");
            }
            return getOrDecapsulateCSK({
              username: userFingerPrint.username,
              dk: userFingerPrint.dk,
              ePrK: userFingerPrint.ePrK,
              eCSK: sourceChatDetails.eCSK,
            });
          })();
        }
        return sourceCSKPromise;
      };

      // Try to get the decrypted blob from local cache first (IndexedDB / OPFS).
      // The sender or viewer will already have it stored from the source chat.
      let cachedBlob: Blob | null = null;

      if (content.media) {
        cachedBlob = await getMediaAttachmentContentFromLocalDB(
          rawAttachmentMeta.attachmentId,
          "content",
        );
      } else if (content.voice) {
        const voiceEntity = await getVoiceAttachmentFromLocalDB(
          rawAttachmentMeta.attachmentId,
        );
        cachedBlob = voiceEntity?.content ?? null;
      } else if (content.file) {
        cachedBlob = await getFileAttachmentContentFromLocalDB(
          rawAttachmentMeta.attachmentId,
        );
      }

      // Non-null is not readable: cached hits are lazy file-backed handles,
      // and pre-liveness-guard builds persisted rows whose backing bytes died
      // (rewritten/evicted). A dead handle here would skip the re-download
      // branch and then throw at materialization — on EVERY retry. Degrade it
      // to a cache miss so the fallback below self-heals with a fresh
      // source-key download, the same way the viewer's serve path does.
      if (cachedBlob && !(await isBlobReadable(cachedBlob))) {
        cachedBlob = null;
      }

      // If not cached locally, download from server and decrypt with source key
      let decryptedBlob: Blob | null = cachedBlob;

      if (!decryptedBlob) {
        const sourceCSK = await getSourceCSK();
        const attachmentUrl = processAttachmentBucketUrl(
          rawAttachmentMeta.attachmentId,
        );
        const eBlob = await requestBlob(attachmentUrl);

        if (eBlob && eBlob.size > 0) {
          decryptedBlob = await decryptMessageAttachment(
            sourceCSK,
            eBlob,
            rawAttachmentMeta.fileType,
          );
        }
      }

      // Fail loudly — don't send a message referencing an attachment that
      // the target chat members won't be able to decrypt.
      if (!decryptedBlob) {
        throw new Error("Failed to obtain attachment for re-encryption");
      }

      // Re-encrypt with target chat's key and upload under a new ID, all under
      // the send cap. The eager arrayBuffer() MATERIALIZE below is a full-size
      // copy (a cache hit is a LAZY OPFS/IndexedDB handle, not bytes in memory;
      // storage eviction can delete that backing file mid-forward — the source is
      // old, first in the oldest-first sweep — after which every read throws and
      // the local cache write would persist a dead blob; one eager copy immunizes
      // the rest of the forward), so it belongs inside the capped span alongside
      // the encrypt+upload. Returns the materialized blob for the cache write below.
      const reEncryptedAttachmentId = generateUuid7();
      decryptedBlob = await runBlobSend(
        async () => {
          const materialized = new Blob([await decryptedBlob!.arrayBuffer()], {
            type: decryptedBlob!.type || rawAttachmentMeta.fileType,
          });
          const file = new File(
            [materialized],
            rawAttachmentMeta.filename || "attachment",
            { type: rawAttachmentMeta.fileType },
          );
          const encryptedBlob = await encryptMessageAttachment(
            chatDetails.eCSK,
            chatDetails.currentEpoch ?? 0,
            chatId,
            file,
          );
          await postAttachment({
            attachment_id: reEncryptedAttachmentId,
            filename: rawAttachmentMeta.filename || "attachment",
            chat_id: chatId,
            encryptedAttachment: encryptedBlob,
          });
          return materialized;
        },
        { sizeBytes: decryptedBlob!.size },
      );
      newAttachmentId = reEncryptedAttachmentId;

      // Cache the decrypted blob in local DB for the target chat
      if (content.media) {
        await addMediaAttachmentToLocalDB({
          id: reEncryptedAttachmentId,
          chatId,
          messageId: _clientId,
          timestamp: now,
          content: decryptedBlob,
          fileType: rawAttachmentMeta.fileType,
          filename: rawAttachmentMeta.filename,
        });
      } else if (content.voice) {
        await addVoiceAttachmentToLocalDB({
          id: reEncryptedAttachmentId,
          chatId,
          messageId: _clientId,
          timestamp: now,
          content: decryptedBlob,
          fileType: rawAttachmentMeta.fileType,
          filename: rawAttachmentMeta.filename,
        });
      } else if (content.file) {
        await addFileAttachmentToLocalDB({
          id: reEncryptedAttachmentId,
          chatId,
          messageId: _clientId,
          timestamp: now,
          content: decryptedBlob,
          fileType: rawAttachmentMeta.fileType,
          filename: rawAttachmentMeta.filename,
          fileSize: rawAttachmentMeta.fileSize,
        });
      }

      // Re-encrypt the thumbnail blob (media attachments only).
      // Best-effort: the thumbnail is cosmetic — a dead cached handle, a
      // failed download, or a failed encrypt/upload must not fail a forward
      // whose main attachment already re-uploaded. Any failure degrades to
      // forwarding without a thumbnail (cleared reference).
      if (content.media && rawAttachmentMeta.attachmentThumbnailId) {
        try {
          // Try local cache first
          let decryptedThumbnail: Blob | null =
            await getMediaAttachmentContentFromLocalDB(
              rawAttachmentMeta.attachmentId,
              "thumbnail",
            );

          // Same dead-handle degradation as the main blob above.
          if (
            decryptedThumbnail &&
            !(await isBlobReadable(decryptedThumbnail))
          ) {
            decryptedThumbnail = null;
          }

          if (!decryptedThumbnail) {
            const sourceCSK = await getSourceCSK();
            const thumbnailUrl = processAttachmentBucketUrl(
              rawAttachmentMeta.attachmentThumbnailId,
            );
            const eThumbnail = await requestBlob(thumbnailUrl);

            if (eThumbnail && eThumbnail.size > 0) {
              decryptedThumbnail = await decryptMessageAttachment(
                sourceCSK,
                eThumbnail,
                "image/webp",
              );
            }
          }

          if (decryptedThumbnail) {
            const reEncryptedThumbnailId = generateUuid7();
            const thumbnailFile = new File([decryptedThumbnail], "thumbnail", {
              type: "image/webp",
            });
            const encryptedThumbnail = await encryptMessageAttachment(
              chatDetails.eCSK,
              chatDetails.currentEpoch ?? 0,
              chatId,
              thumbnailFile,
            );
            await postAttachment({
              attachment_id: reEncryptedThumbnailId,
              filename: "thumbnail",
              chat_id: chatId,
              encryptedAttachment: encryptedThumbnail,
            });

            newThumbnailId = reEncryptedThumbnailId;

            // Update the existing media attachment entity with the thumbnail
            await updateMediaAttachmentContent(
              newAttachmentId,
              "thumbnail",
              decryptedThumbnail,
            );
          } else {
            // Clear the thumbnail reference — don't leave a stale ID pointing
            // to a blob encrypted with the source chat's key.
            newThumbnailId = "";
          }
        } catch {
          // Same clearing as the miss branch: the message forwards without a
          // thumbnail instead of failing outright over a preview image.
          newThumbnailId = "";
        }
      }
    }

    // Update the optimistic message with re-encrypted attachment IDs
    if (rawAttachmentMeta) {
      const updatedContent = {
        ...messageEntity.content,
        ...(content.media && {
          media: {
            ...content.media,
            attachmentId: newAttachmentId,
            attachmentThumbnailId: newThumbnailId,
            // Re-assert the resets: this spread starts from the SOURCE meta,
            // which would clobber the optimistic row's cleared session markers
            // back in (see the messageEntity build above).
            isLargeAttachment: false,
            uploadState: undefined,
          },
        }),
        ...(content.file && {
          file: { ...content.file, attachmentId: newAttachmentId },
        }),
        ...(content.voice && {
          voice: { ...content.voice, attachmentId: newAttachmentId },
        }),
      };
      await updateMessageByClientIdInLocalDB(_clientId, {
        content: updatedContent,
      });
      // Also update the in-memory entity for the server payload below
      messageEntity.content = updatedContent;
    }

    // ................
    // SERVER STUFF
    // ................

    // Encrypt text with target chat's key
    const { encryptedContent: fwdEncContent, key_epoch: fwdKeyEpoch } =
      await encryptMessageContent(
        chatDetails.eCSK,
        chatDetails.currentEpoch ?? 0,
        chatId,
        content.text || "",
      );

    // Encrypt the attachment meta descriptor (with new IDs) with target chat's key.
    // isLargeAttachment/uploadState are RESET, not copied: they describe the
    // SOURCE's chunked-upload session, but a forward re-uploads as one plain
    // bucket object. Copying them sent every >50MB forwarded video down the
    // streamed-playback path against an upload session that never existed
    // (error card), and a copied "failed" uploadState mislabelled a perfectly
    // good forward as a failed upload.
    const updatedAttachmentMeta = rawAttachmentMeta
      ? {
          ...rawAttachmentMeta,
          attachmentId: newAttachmentId,
          isLargeAttachment: false,
          uploadState: undefined,
          ...("attachmentThumbnailId" in rawAttachmentMeta && {
            attachmentThumbnailId: newThumbnailId,
          }),
        }
      : null;

    const fwdEncAttachmentMeta = updatedAttachmentMeta
      ? (
          await encryptMessageContent(
            chatDetails.eCSK,
            chatDetails.currentEpoch ?? 0,
            chatId,
            JSON.stringify(updatedAttachmentMeta),
          )
        ).encryptedContent
      : "";

    const encryptedMessage: Partial<EncryptedMessage> = {
      client_id: _clientId,
      chat_id: chatId,
      sender: userFingerPrint.username,
      content: fwdEncContent,
      key_epoch: fwdKeyEpoch,
      is_edited: false,
      category: MessageCategory.MESSAGE,
      comment_count: null,
      thread_id: null,
      attachment_meta: fwdEncAttachmentMeta,
    };

    const messageType =
      chatDetails.type === ChatType.GROUP
        ? MessageType.GROUP
        : chatDetails.type === ChatType.P2P
          ? MessageType.P2P
          : MessageType.NOTE;

    const serverMessageIds = await postMessage(messageType, encryptedMessage);
    const serverMessageId = serverMessageIds[0];
    const serverMessageTimestamp = extractTimestampFromUUID7(serverMessageId);

    const localDbMessage = await getMessageByClientIdFromLocalDB(_clientId);
    if (
      localDbMessage &&
      (localDbMessage.sendingStatus === MessageSendingStatus.DELIVERED ||
        localDbMessage.sendingStatus === MessageSendingStatus.READ)
    ) {
      return;
    }

    await updateMessageByServerIdInLocalDB(_clientId, {
      id: serverMessageId,
      timestamp: serverMessageTimestamp,
      sendingStatus: MessageSendingStatus.SENT,
    });
  } catch (error) {
    await markSendFailed(_clientId);
    throw new Error(
      `Error forwarding message: ${error instanceof Error ? error.message : String(error)}`,
    );
  }
};

/**
 * Refreshes a message by fetching a fresh copy from the API and updating it in the local database
 *
 * How it works:
 * 1. Retrieves the chat to access the encryption key
 * 2. Fetches the message from the API using processParentMessage
 * Key features:
 * - Simplified interface requiring only messageId
 * - Provides real-time message data updates
 * - Handles encryption/decryption automatically
 * - Updates local database with fresh data
 * - Supports abort signal for cancellation
 */
// export const updateMessageOnReactionEvent = async (
//   messageId: string,
//   signal?: AbortSignal,
// ): Promise<MessageEntity | undefined> => {
//   // Check if abort signal is triggered
//   if (signal?.aborted) {
//     return undefined;
//   }

//   // Check if user is authenticated
//   const userFingerPrint = isUserAuthenticated();
//   if (!userFingerPrint) {
//     throw new Error("Auth not present");
//   }

//   try {
//     // Get the message from local database to retrieve necessary information
//     const message = await getMessageByIdFromLocalDB(messageId);
//     if (!message) {
//       return;
//     }

//     const chatId = message.chatId;
//     const category = message.category;

//     // Get the chat to access the encryption key
//     const chat = await getChatFromLocalDB(chatId);
//     if (!chat || !chat.eCSK) {
//       throw new Error("Chat not found or missing encryption key");
//     }

//     // Fetch fresh message from API
//     const freshMessage = await processParentMessage({
//       messageId,
//       chatId,
//       category,
//       eCSK: chat.eCSK,
//       signal,
//     });

//     if (!freshMessage) {
//       // throw new Error("Failed to refresh message");
//       // it can return undefined if parrent message already in indexxedDB
//       return undefined; // TO DO => TEMP FIX
//     }

//     /**
//      * Purpose: Updates the userReaction field with fresh data from the server
//      *
//      * Note: This currently clears any pending/optimistic state regardless of
//      * which user generated the reaction event. This may cause a brief UI flicker
//      * when other users react to messages.
//      *
//      * TODO: Implement selective userReaction updates by:
//      * 1. Comparing event user ID with current user ID
//      * 2. Preserving optimistic state when events come from other users
//      * 3. Only updating userReaction when the current user triggered the event
//      */
//     const fieldsToUpdate = {
//       reactions: freshMessage.reactions,
//       userReaction: freshMessage.userReaction,
//     };

//     await updateMessageInLocalDB(messageId, fieldsToUpdate);

//     return freshMessage;
//   } catch (error) {
//     // Re-throw the error for the caller to handle
//     throw error instanceof Error
//       ? error
//       : new Error("Failed to refresh message");
//   }
// };

export const deleteMessageFromChat = async ({
  messageId,
  signal,
}: {
  messageId: string;
  signal?: AbortSignal;
}): Promise<void> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // if no payload
  if (!messageId) {
    return;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  // check if message exists in local db
  const message = await getMessageByServerIdFromLocalDB(messageId);

  if (!message) {
    return;
  }

  // prevent delete after immediate sending
  if (message.sendingStatus === MessageSendingStatus.PENDING) {
    return;
  }

  // if chat id is active
  const activeChatId = useNavigationState.getState().activeChatId;
  if (message?.chatId !== activeChatId) {
    return;
  }

  const activeThreadId = useNavigationState.getState().activeThreadId;

  // // reference to pinned msgs methods
  // const removePinnedMessageForChat =
  //   usePinnedMessageState.getState().removePinnedMessageForChat;

  // TO DO => PREVENT DOUBLE updates in LOCAL DB (middleware/socket events) -
  try {
    // check if chat exists in local db
    const chatDetails = await getChatFromLocalDB(message.chatId);
    if (message.chatId !== chatDetails?.id) {
      return;
    }

    const messageType =
      chatDetails.type === ChatType.GROUP
        ? MessageType.GROUP
        : chatDetails.type === ChatType.P2P
          ? MessageType.P2P
          : MessageType.NOTE;

    // OPTIMISTIC  STUFF

    // remove the message from local db
    await removeMessageByServerIdFromLocalDB(messageId);

    // remove from pinned msgs
    await removePinnedMessageByServerIdFromLocalDB(messageId);

    // // remove the link data for this message
    // await deleteLinkByIdFromLocalDB(messageId);

    // TO DO => talk with team
    // - on delete pinned msg, do we also make "unpin" POST to server?
    // - or it is automatically handeled?

    // SERVER STUFF

    // delete message from server
    await deleteMessage({
      message_id: message.id,
      chat_id: message.chatId,
      category: activeThreadId
        ? MessageCategory.COMMENT
        : MessageCategory.MESSAGE,
      message_type: messageType,
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // revert optimistic update = add the message back to local db
    await addMessageToLocalDB(message);

    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Error removing message");
  }
};

// TO DO = To be continued ..
// export const resendMessageInChat = async ({
//   message,
//   signal,
// }: {
//   message: MessageEntity;
//   signal?: AbortSignal;
// }): Promise<void> => {
//   // extra check on abort controller
//   if (signal?.aborted) {
//     return;
//   }

//   // if no payload
//   if (!message) {
//     return;
//   }

//   // if chat id is active
//   const activeChatId = useNavigationState.getState().activeChatId;
//   if (message.chatId !== activeChatId) {
//     return;
//   }

//   // extra check if auth is present
//   const userFingerPrint = isUserAuthenticated();
//   if (!userFingerPrint) {
//     throw new Error("Auth not present");
//   }

//   const newMessageId = uuidv7();
//   const newMessageTimestamp = new Date().toISOString();

//   // TO DO => PREVENT DOUBLE updates in LOCAL DB (middleware/socket events) -
//   try {
//     // check if chat exists in local db
//     const chatDetails = await getChatFromLocalDB(message.chatId);
//     if (message.chatId !== chatDetails?.id) {
//       return;
//     }

//     // OPTIMISTIC STUFF

//     // build new msg entity for optimistic update in local db
//     // we only update with new timestamp and sending status
//     const newMessageEntity: MessageEntity = {
//       ...message,
//       timestamp: newMessageTimestamp,
//       sendingStatus: MessageSendingStatus.PENDING,
//     };

//     await updateMessageInLocalDB(message.id, newMessageEntity);
//     await updateChatInLocalDB(message.chatId, {
//       lastMessage: newMessageEntity,
//       lastMessageTimestamp: newMessageTimestamp,
//     });

//     // SERVER STUFF

//     // if only text message
//     if (
//       message.content.text &&
//       !message.content.media &&
//       !message.content.file
//     ) {
//       // TO DO => re post message with text content method
//     }

//     // if media attachment message
//     if (message.content.media) {
//       // TO DO => re post message with media attachment method
//     }

//     // if file attachment message
//     if (message.content.file) {
//       // TO DO => re post message with file attachment method
//     }

//     // const encryptedContent = await encryptMessageContent(
//     //   chatDetails.eCSK,
//     //   content,
//     // );

//     // const encryptedMessage: Partial<EncryptedMessage> = {
//     //   id: newMessageId,
//     //   chat_id: chatId,
//     //   sender: userFingerPrint.username,
//     //   content: encryptedContent,
//     //   // sent_at: newMessageTimestamp,
//     //   is_edited: false,
//     //   // attachment_id: "", // deprecated
//     //   category: MessageCategory.MESSAGE,
//     //   // has_replies: false,
//     //   // has_reactions: false,
//     //   // parent_id: "",
//     //   parent_sent_at: "",
//     //   // parent_category: MessageCategory.MESSAGE,
//     //   attachment_meta: "",
//     //   // profile_image: "",
//     //   has_links,
//     //   thumbnail: "",
//     //   // reactions: {},
//     //   // user_reactions: [],
//     // };

//     // const messageType =
//     //   chatDetails.type === ChatType.GROUP
//     //     ? MessageType.GROUP
//     //     : chatDetails.type === ChatType.P2P
//     //       ? MessageType.P2P
//     //       : MessageType.NOTE;

//     // await postMessage(messageType, encryptedMessage);
//     // eslint-disable-next-line @typescript-eslint/no-unused-vars
//   } catch (error) {
//     // TO DO => check if we have to remove any links from db
//     // await updateMessageInLocalDB(newMessageId, {
//     //   sendingStatus: MessageSendingStatus.FAILED,
//     // });
//     // if (error instanceof Error && error.message) throw error;
//     throw new Error("Error sending message");
//   }
// };

export const togglePinMessage = async ({
  id,
  chat_id,
  sent_at,
  category,
  signal,
}: {
  id: string;
  chat_id: string;
  sent_at: string;
  category: MessageCategory;
  signal?: AbortSignal;
}): Promise<void> => {
  // extra check on abort controller
  if (signal?.aborted) {
    return;
  }

  // if no payload
  if (!id || !chat_id || !sent_at) {
    return;
  }

  // extra check if auth is present
  const userFingerPrint = isUserAuthenticated();
  if (!userFingerPrint) {
    throw new Error("Auth not present");
  }

  // check if message exists in local db
  const message = await getMessageByServerIdFromLocalDB(id);
  if (!message) {
    return;
  }
  // reference to pinned state methods
  // const removePinnedMessageForChat =
  //   usePinnedMessageState.getState().removePinnedMessageForChat;
  // const addPinnedMessageForChat =
  //   usePinnedMessageState.getState().addPinnedMessageForChat;

  const oldValue = message.isPinned;

  // if chat id is active
  const activeChatId = useNavigationState.getState().activeChatId;
  if (message?.chatId !== activeChatId) {
    return;
  }

  // TO DO => PREVENT DOUBLE updates in LOCAL DB (middleware/socket events) -
  try {
    // OPTIMISTIC  STUFF

    // update the message in local db
    await updateMessageByServerIdInLocalDB(id, {
      isPinned: !oldValue,
    });

    if (oldValue) {
      await removePinnedMessageByServerIdFromLocalDB(id);
    } else {
      await addPinnedMessageToLocalDB(message);
    }

    // SERVER STUFF

    // update message on server
    await pinMessage(!oldValue, {
      id: id,
      chat_id: chat_id,
      category: category,
      sender: userFingerPrint.username,
      content: "",
      is_edited: false,
      parent_id: "",
      attachment_meta: "",
      reactions: {},
      user_reactions: [],
      tags: [],
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (error) {
    // revert optimistic update
    await updateMessageByServerIdInLocalDB(message.id, {
      isPinned: oldValue,
    });

    if (oldValue) {
      await addPinnedMessageToLocalDB(message);
    } else {
      await removePinnedMessageByServerIdFromLocalDB(id);
    }

    if (error instanceof Error && error.message) {
      throw error;
    }
    throw new Error("Error pinning message");
  }
};

/**
 * Validates that message IDs are proper UUID7 format
 * @param messages - Array of messages to validate
 * @returns Array of valid messages (filters out invalid UUID7 IDs)
 */
export const validateMessageIds = (
  messages: MessageEntity[],
): MessageEntity[] => {
  return messages.filter((msg) => {
    // Validate UUID7 format using built-in uuid library
    const isValid = isValidUUID7(msg.id);

    if (!isValid) {
      // eslint-disable-next-line no-console
      console.warn("Filtering out message with invalid UUID7 ID:", msg.id);
      return false;
    }

    return true;
  });
};
